/* ============================================================================
   Kernel Theme - theme.js
   Vanilla JS, no framework. Uses custom elements + Shopify AJAX cart APIs.
   ============================================================================ */
(() => {
  'use strict';

  const routes = window.theme?.routes || {};
  const cartType = window.theme?.cartType || 'drawer';

  /* ---------- Tiny helpers ---------- */
  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const debounce = (fn, wait = 250) => {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  // Uses the shopper's currency format from layout/theme.liquid, which adds
  // the currency code (e.g. "$26.00 CAD") outside the store currency.
  const formatMoney = (cents) => {
    const n = (Number(cents) || 0) / 100;
    const fmt = String((window.theme && window.theme.moneyFormat) || '${{amount}}').replace(/<[^>]*>/g, '');
    const group = (v, d) => v.toFixed(d).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return group(n, 0);
      if (key === 'amount_with_comma_separator') return n.toFixed(2).replace('.', ',');
      return group(n, 2);
    });
  };

  const fetchJSON = async (url, opts = {}) => {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(opts.headers || {}) },
      ...opts
    });
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    return res.json();
  };

  const escapeHTML = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  /* Unified progress bar — one track shows Free Shipping + Free Gift milestones
     together. Computes the qualifying subtotal (gift line excluded), positions
     the fill + "you" marker, swaps the message, and toggles milestone unlocked
     state classes. */
  // Boxes one cart line counts toward free shipping and the free gift. Mirrors
  // cart-drawer.liquid: per-handle counts from the drawer's "Boxes per unit"
  // setting (data-box-counts), 0 for anything that doesn't ship, else 1.
  function lineBoxes(item) {
    if (item.requires_shipping === false) return 0;
    let counts = {};
    try { counts = JSON.parse(($('#CartDrawer') || {}).dataset?.boxCounts || '{}'); } catch {}
    const per = counts[item.handle];
    return (item.quantity || 0) * (typeof per === 'number' ? per : 1);
  }

  // Minimum quantity for a product's cart line, from Theme settings → Cart
  // (min_line_qty, "handle:qty, handle:qty"). The warehouse only stocks
  // 12-packs, so a flavor sold two at a time goes in as quantity 2.
  function minQtyFor(handle) {
    for (const rule of String(window.theme?.minQty || '').split(',')) {
      const [h, q] = rule.split(':').map(s => (s || '').trim());
      if (h && h === handle) return parseInt(q, 10) || 1;
    }
    return 1;
  }

  // Add-to-cart quantity for flavors sold two at a time: the pair first, then
  // one box at a time once the flavor is in the cart (24, 36, 48... bags).
  function syncMinQtyInputs(cart) {
    $$('input[name="quantity"][data-min-qty]').forEach(input => {
      const id = input.form?.querySelector('[name="id"]')?.value;
      const inCart = (cart.items || []).some(i => !i.properties?._bundle && String(i.variant_id) === String(id) && i.quantity >= minQtyFor(i.handle));
      const next = inCart ? 1 : (parseInt(input.dataset.minQty, 10) || 1);
      // The drawer's quick-add row is rendered once, so its price follows the add quantity here.
      const price = input.closest('.cart-drawer__quick-add-item')?.querySelector('[data-unit-price]');
      if (price) price.textContent = formatMoney(parseInt(price.dataset.unitPrice, 10) * next);
      if (String(input.min) === String(next) && String(input.value) === String(next)) return syncDecrement(input);
      input.min = next;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function bagsPerUnit() {
    return parseInt(($('#CartDrawer') || {}).dataset?.defaultBagsPerUnit, 10) || 12;
  }

  function isFreeGiftLine(item) {
    const p = item.properties || {};
    return p._free_gift === 'true' || p._free_gift === true;
  }

  // Boxes the whole cart counts toward the minimum, the free gift and free
  // shipping: every line's lineBoxes except the free gift itself.
  function cartBoxes(cart) {
    return ((cart && cart.items) || []).reduce((sum, i) => isFreeGiftLine(i) ? sum : sum + lineBoxes(i), 0);
  }

  // The order minimum in boxes (cart drawer setting "Minimum boxes per order").
  // It applies to the cart as a whole, so a 12-pack can ride along with any 24.
  function minCartBoxes() {
    return parseInt(($('#CartDrawer') || {}).dataset?.minCartBoxes, 10) || 0;
  }

  // Bags in one unit of a cart line: its box count (Boxes per unit) x bags per box.
  function lineBagsPerUnit(item) {
    return lineBoxes({ ...item, quantity: 1 }) * bagsPerUnit();
  }

  // An add quantity can't go below its min (24 bags for a first add), so the
  // minus goes dead there rather than taking taps that do nothing. Cart lines
  // (updates[]) are left alone: their minus removes the line.
  function syncDecrement(input) {
    const dec = input.closest('.quantity-input')?.querySelector('[data-quantity-decrement]');
    if (dec) dec.disabled = (parseInt(input.value, 10) || 0) <= (parseInt(input.min, 10) || 0);
  }
  $$('input[name="quantity"]').forEach(syncDecrement);

  // Popcorn shows its quantity in bags (24, 36, 48...).
  document.addEventListener('change', (e) => {
    const input = e.target.closest?.('input[name="quantity"], input[name="updates[]"]');
    if (input?.name === 'quantity') syncDecrement(input);
    const label = input?.closest('.quantity-input')?.querySelector('[data-qty-bags]');
    if (label) label.textContent = `${(parseInt(input.value, 10) || 0) * (parseInt(label.dataset.bagsPer, 10) || 12)} bags`;
  });

  // Free-shipping test: each shopper keeps one group (A control, B free from
  // 3 boxes) and the cart carries it as _ship_test, which the R30 Hub
  // "Shipping rules" Function reads at checkout.
  const shipTestGroup = (() => {
    if (!window.theme?.shipTest) return '';
    let group = window.theme.shipTestCart || '';
    try { group = group || localStorage.getItem('gk_ship_test') || ''; } catch {}
    if (group !== 'A' && group !== 'B') group = Math.random() < 0.5 ? 'A' : 'B';
    try { localStorage.setItem('gk_ship_test', group); } catch {}
    return group;
  })();
  if (shipTestGroup) {
    if (shipTestGroup === 'B' && window.theme.shipTestNoteB) {
      $$('.product-form__atc-note').forEach(el => { el.textContent = window.theme.shipTestNoteB; });
    }
    if (window.theme.shipTestCart !== shipTestGroup) {
      fetchJSON(`${routes.cart_update_url}.js`, { method: 'POST', body: JSON.stringify({ attributes: { _ship_test: shipTestGroup } }) })
        .then(updateUnifiedProgress)
        .catch(err => console.warn('[cart] ship test tag failed', err));
    }
  }

  function updateUnifiedProgress(cart) {
    const drawer = $('#CartDrawer');
    if (!drawer) return;
    const wrap = drawer.querySelector('[data-cart-progress]');
    if (!wrap) return;

    // An all-subscription cart, or free-shipping test group B, ships free from
    // fewer boxes (R30 Hub "Shipping rules" Function); mirrors cart-drawer.liquid.
    const paidLines = (cart && cart.items || []).filter(i => !isFreeGiftLine(i));
    const subCart = (paidLines.length > 0 && paidLines.every(i => i.selling_plan_allocation)) || shipTestGroup === 'B';
    const baseShipping = parseInt(drawer.dataset.shippingThreshold || '0', 10);
    const subShipping = parseInt(drawer.dataset.subShippingThreshold || '0', 10);
    const shippingThreshold = subCart && baseShipping > 0 && subShipping > 0 ? subShipping : baseShipping;
    const giftThreshold = parseInt(drawer.dataset.freeGiftThreshold || '0', 10);
    const max = Math.max(shippingThreshold, giftThreshold);
    if (max <= 0) return;
    const hasBoth = shippingThreshold > 0 && giftThreshold > 0;
    // Whichever threshold is lower sits at 50% of the bar; the higher one at 100%.
    const giftIsLower = hasBoth && giftThreshold < shippingThreshold;
    const lowerThreshold = hasBoth ? Math.min(shippingThreshold, giftThreshold) : (shippingThreshold || giftThreshold);
    const higherThreshold = hasBoth ? Math.max(shippingThreshold, giftThreshold) : (shippingThreshold || giftThreshold);

    // Thresholds count BOXES (see lineBoxes), excluding the gift line so
    // removing/re-adding it can't oscillate the bar.
    const qualifying = (cart && cart.items || []).reduce((sum, i) => {
      const p = i.properties || {};
      const isGift = p._free_gift === 'true' || p._free_gift === true;
      return isGift ? sum : sum + lineBoxes(i);
    }, 0);

    // Step-wise fill so the bar's visual midpoint aligns with the lower
    // milestone label (which is fixed at 50% of the bar when both are set).
    let pct;
    if (hasBoth) {
      if (qualifying >= higherThreshold) pct = 100;
      else if (qualifying >= lowerThreshold) {
        const over = qualifying - lowerThreshold;
        const seg = higherThreshold - lowerThreshold;
        pct = 50 + (seg > 0 ? (over * 50) / seg : 50);
      } else {
        pct = (qualifying * 50) / lowerThreshold;
      }
    } else {
      pct = Math.min(100, (qualifying / max) * 100);
    }
    pct = Math.max(0, Math.min(100, pct));
    const shippingUnlocked = shippingThreshold > 0 && qualifying >= shippingThreshold;
    const giftUnlocked = giftThreshold > 0 && qualifying >= giftThreshold;

    // Fill width.
    const fillEl = wrap.querySelector('[data-cart-progress-fill]');
    if (fillEl) fillEl.style.width = pct + '%';

    // Milestone unlocked classes.
    wrap.querySelectorAll('[data-cart-progress-milestone]').forEach(el => {
      const which = el.dataset.cartProgressMilestone;
      const unlocked = which === 'shipping' ? shippingUnlocked : giftUnlocked;
      el.classList.toggle('is-unlocked', unlocked);
    });
    wrap.classList.toggle('is-shipping-unlocked', shippingUnlocked);
    wrap.classList.toggle('is-gift-unlocked', giftUnlocked);

    // "Add a 12-pack" shows from the order minimum until the last milestone.
    const addon = drawer.querySelector('[data-cart-addon]');
    if (addon) {
      addon.hidden = !(qualifying >= minCartBoxes() && qualifying < max);
      addon.classList.toggle('is-gift-unlocked', !(giftThreshold > 0) || giftUnlocked);
      // Same rule as the Liquid render: follow the newest paid line's plan.
      const lead = (cart && cart.items || []).find(i => !isFreeGiftLine(i));
      const planId = lead && lead.selling_plan_allocation ? String(lead.selling_plan_allocation.selling_plan.id) : '';
      addon.querySelectorAll('[data-addon-plans]').forEach(form => {
        let plans = {};
        try { plans = JSON.parse(form.dataset.addonPlans || '{}'); } catch (e) {}
        const usePlan = planId && plans[planId] != null ? planId : '';
        const planInput = form.querySelector('[data-addon-plan]');
        if (planInput) planInput.value = usePlan;
        const priceEl = form.querySelector('[data-addon-price]');
        if (priceEl) priceEl.textContent = formatMoney(usePlan ? plans[usePlan] : parseInt(form.dataset.addonPriceOnce, 10));
      });
    }

    // Status message — same logic as the Liquid render so SSR + JS stay in sync.
    const messageEl = wrap.querySelector('[data-cart-progress-message]');
    if (messageEl) {
      const bagsPer = bagsPerUnit();
      const belowTmpl = drawer.dataset.progressBelow || 'Spend [amount] more to unlock FREE shipping';
      const transitionalTmpl = drawer.dataset.progressTransitional || 'FREE shipping unlocked — add [amount] for a FREE gift!';
      const belowGiftTmpl = drawer.dataset.progressBelowGift || 'Spend [amount] more to unlock a FREE gift';
      const transitionalAfterGiftTmpl = drawer.dataset.progressTransitionalAfterGift || 'FREE gift unlocked — add [amount] for FREE shipping!';
      const bothUnlockedMsg = drawer.dataset.progressBothUnlocked || '🎉 You unlocked FREE shipping + a FREE gift!';

      let msg;
      if (qualifying < minCartBoxes() && drawer.dataset.progressStart) {
        msg = drawer.dataset.progressStart;
      } else if (hasBoth) {
        if (qualifying >= higherThreshold) {
          msg = bothUnlockedMsg;
        } else if (qualifying >= lowerThreshold) {
          const rem = Math.max(0, higherThreshold - qualifying);
          msg = (giftIsLower ? transitionalAfterGiftTmpl : transitionalTmpl).replace('[amount]', String(rem)).replace('[bags]', String(rem * bagsPer));
        } else {
          const rem = Math.max(0, lowerThreshold - qualifying);
          if (subCart && lowerThreshold === higherThreshold && drawer.dataset.progressSubBelow) {
            msg = drawer.dataset.progressSubBelow.replace('[amount]', String(rem));
          } else {
            msg = (giftIsLower ? belowGiftTmpl : belowTmpl).replace('[amount]', String(rem)).replace('[bags]', String(rem * bagsPer));
          }
        }
      } else if (shippingThreshold > 0) {
        if (shippingUnlocked) {
          msg = bothUnlockedMsg;
        } else {
          const rem = Math.max(0, shippingThreshold - qualifying);
          msg = belowTmpl.replace('[amount]', String(rem)).replace('[bags]', String(rem * bagsPer));
        }
      } else if (giftThreshold > 0) {
        if (giftUnlocked) {
          msg = drawer.dataset.progressGiftUnlocked || bothUnlockedMsg;
        } else {
          const rem = Math.max(0, giftThreshold - qualifying);
          msg = belowGiftTmpl.replace('[amount]', String(rem)).replace('[bags]', String(rem * bagsPer));
        }
      }
      if (msg != null) messageEl.textContent = msg;
      // Shrink the font-size if the rendered message would overflow its
      // container — keeps the full text on one line at any drawer width.
      fitProgressMessage(messageEl);
    }
  }

  function fitProgressMessage(el) {
    if (!el) return;
    // Reset to default font-size so we start from the largest, then shrink
    // step-by-step until the text no longer overflows.
    el.style.fontSize = '';
    el.style.whiteSpace = '';
    // Use rAF so the layout is settled before we measure.
    requestAnimationFrame(() => {
      // el.clientWidth is the message box's own width (parent content area —
      // the right thing to compare scrollWidth against). Skip if not visible
      // yet (e.g. drawer is still hidden), the drawer.open handler re-runs us.
      if (el.clientWidth <= 0) return;
      let fontSize = parseFloat(getComputedStyle(el).fontSize);
      const minFontSize = 10; // px floor — anything smaller becomes unreadable
      let safety = 60;
      while (el.scrollWidth > el.clientWidth && fontSize > minFontSize && safety-- > 0) {
        fontSize -= 0.5;
        el.style.fontSize = fontSize + 'px';
      }
      // Still too long at the floor: wrap to a second line rather than clip.
      if (el.scrollWidth > el.clientWidth) el.style.whiteSpace = 'normal';
    });
  }
  // Aliases — older call sites still reference these two names.
  const updateShippingProgress = updateUnifiedProgress;
  const updateGiftProgress = updateUnifiedProgress;

  function renderCartDrawerHTML(cart) {
    if (!cart || cart.item_count === 0) {
      const e = window.theme?.cartEmpty || {};
      return `
        <div class="cart-drawer__empty">
          <p class="cart-drawer__empty-title" style="font-size:clamp(1.6rem,4.5vw,2.6rem);">${escapeHTML(e.heading || "Where'd it all go?")}</p>
          <p class="cart-drawer__empty-text">${escapeHTML(e.text || 'Looks like your cart is empty — time to fill it up with your favorites!')}</p>
          <a href="${escapeHTML(e.buttonUrl || '/collections/all')}" class="button button--primary button--lg">${escapeHTML(e.buttonLabel || 'Shop Now')}</a>
        </div>
      `;
    }
    const trashIconSVG = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>`;

    const items = cart.items.map(item => {
      const hasUpgrade = !item.selling_plan_allocation && Array.isArray(item.product_has_only_default_variant)
        ? false
        : Boolean(item.selling_plan_allocation == null && (item.product?.selling_plan_groups?.length || item.selling_plan_groups_count));
      const itemProps = item.properties || {};
      const isGiftItem = itemProps._free_gift === 'true' || itemProps._free_gift === true;
      const imgHTML = item.image ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.product_title || '')}" width="80" height="80" loading="lazy">` : '';
      // Subscription upgrade button is rendered server-side on initial paint.
      // For AJAX re-renders we don't have selling_plan_groups in /cart.js, so leave it off here.
      return `
      <li class="cart-drawer__item${isGiftItem ? ' cart-drawer__item--free-gift' : ''}" data-cart-item data-key="${escapeHTML(item.key)}"${isGiftItem ? ' data-free-gift' : ''}>
        <div class="cart-drawer__item-row">
          ${isGiftItem
            ? `<div class="cart-drawer__item-image">${imgHTML}</div>`
            : `<a href="${escapeHTML(item.url)}" class="cart-drawer__item-image">${imgHTML}</a>`}
          <div class="cart-drawer__item-info">
            <div class="cart-drawer__item-header">
              <div class="cart-drawer__item-titles">
                ${isGiftItem
                  ? `<span class="cart-drawer__item-title cart-drawer__item-title--static">${escapeHTML(item.product_title)}</span>`
                  : `<a href="${escapeHTML(item.url)}" class="cart-drawer__item-title">${escapeHTML(item.product_title)}</a>`}
                ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-variant">${escapeHTML(item.variant_title)}</p>` : ''}
                ${itemProps._bundle && window.theme?.bundleTag !== false ? `<p class="cart-drawer__item-bundle">Part of ${escapeHTML(itemProps._bundle)}</p>` : ''}
                ${!isGiftItem && item.selling_plan_allocation ? `<span class="cart-drawer__item-subscription"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><path d="M21 3v5h-5"/></svg>${escapeHTML(cleanPlanName(item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name) || 'Subscription')}</span>` : ''}
              </div>
              ${(() => {
                if (isGiftItem) {
                  return `<div class="cart-drawer__item-prices"><p class="cart-drawer__item-price cart-drawer__item-price--free">FREE</p></div>`;
                }
                // Per-bag pricing — parse a leading number from the variant title
                // (e.g. "24 BAGS" → 24). /cart.js doesn't expose metafields, so
                // for single-variant products we fall back to the global
                // theme setting `default_bags_per_unit` (mirrored on the
                // drawer's dataset).
                const m = (item.variant_title || '').match(/^(\d+)/);
                let bags = m ? parseInt(m[1], 10) : 0;
                if (bags <= 0) {
                  const drawer = $('#CartDrawer');
                  bags = drawer ? parseInt(drawer.dataset.defaultBagsPerUnit || '0', 10) : 0;
                }
                const perBagLine = bags > 1
                  ? `<p class="cart-drawer__item-price-per">${escapeHTML(formatMoney(Math.round(item.final_price / bags)))} / bag</p>`
                  : '';
                return `<div class="cart-drawer__item-prices">
                  <p class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</p>
                  ${perBagLine}
                </div>`;
              })()}
            </div>
            ${isGiftItem ? '' : `<div class="cart-drawer__item-controls">
              <div class="quantity-input${itemProps._bundle ? ' quantity-input--locked' : ''}">
                <button type="button" class="quantity-input__button" data-quantity-decrement aria-label="Decrease quantity">&minus;</button>
                <input class="quantity-input__field" type="${lineBagsPerUnit(item) > 1 ? 'hidden' : 'number'}" name="updates[]" value="${item.quantity}" min="0" data-quantity-input data-key="${escapeHTML(item.key)}">${lineBagsPerUnit(item) > 1 ? `<span class="quantity-input__field quantity-input__bags" data-qty-bags data-bags-per="${lineBagsPerUnit(item)}" style="width:auto;min-width:4.6em;display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;">${item.quantity * lineBagsPerUnit(item)} bags</span>` : ''}
                <button type="button" class="quantity-input__button" data-quantity-increment aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="cart-drawer__item-trash" data-cart-remove data-key="${escapeHTML(item.key)}" aria-label="Remove ${escapeHTML(item.product_title)}">${trashIconSVG}</button>
            </div>`}
          </div>
        </div>
      </li>
    `;
    }).join('');

    return `<ul class="cart-drawer__items" role="list">${items}</ul>`;
  }

  function toggleCartDrawerChrome(cart) {
    const isEmpty = !cart || cart.item_count === 0;
    $$('[data-cart-drawer-footer]').forEach(el => { el.hidden = isEmpty; });
    // Quick-add stays visible whether cart has items or not
  }

  /* ---------- Express checkout buttons in two rows ----------
     Shopify draws the drawer's express buttons inside a closed shadow root as
     one column (or, under .additional-checkout-buttons--horizontal, one row),
     so CSS can't wrap them. Instead we swap its element for two copies, each
     handed part of its wallet-configs, both in one-row mode. If that attribute
     ever stops being a list of {name} objects we leave Shopify's element alone
     and the buttons stay stacked. Which wallets arrive varies by browser. */
  const EXPRESS_TOP_ROW = ['shop_pay', 'apple_pay', 'paypal'];

  function splitExpressButtons(host, wrap) {
    let configs;
    try { configs = JSON.parse(host.getAttribute('wallet-configs')); } catch (e) { return; }
    if (!Array.isArray(configs) || !configs.every(c => c && typeof c.name === 'string')) return;
    const top = EXPRESS_TOP_ROW.map(name => configs.find(c => c.name === name)).filter(Boolean);
    const bottom = configs.filter(c => !EXPRESS_TOP_ROW.includes(c.name));
    if (!top.length || !bottom.length) return;
    // Shopify looks for the horizontal class anywhere in the document when a
    // cart element renders, so it goes on before the copies connect. The drawer
    // is the theme's only cart express-button slot, so nothing else is affected.
    wrap.classList.add('additional-checkout-buttons--horizontal', 'cart-drawer__express--rows');
    host.replaceWith(...[top, bottom].map(row => {
      const copy = host.cloneNode(false);
      copy.setAttribute('wallet-configs', JSON.stringify(row));
      copy.setAttribute('data-express-row', '');
      return copy;
    }));
  }

  function bindExpressRows() {
    const wrap = $('.cart-drawer__express');
    if (!wrap) return;
    const split = () => $$('shopify-accelerated-checkout-cart:not([data-express-row])', wrap).forEach(host => splitExpressButtons(host, wrap));
    split();
    new MutationObserver(split).observe(wrap, { childList: true, subtree: true });
  }

  /* updateGiftProgress was merged into updateUnifiedProgress. The alias at
     the bottom of updateUnifiedProgress keeps old call sites working. */

  /* ---------- Free gift with purchase ----------
     Auto-adds (and auto-removes) a free-gift variant when the qualifying cart
     subtotal — i.e. everything EXCEPT the gift itself — crosses the merchant's
     threshold. Settings come from data attributes on <cart-drawer> set by Liquid.
     We tag the gift line item with `properties[_free_gift]=true` so the cart
     template can lock controls and the qualifying subtotal can skip it. */
  let _freeGiftSyncing = false;
  async function syncFreeGift(cart) {
    if (_freeGiftSyncing) return cart;
    const drawer = $('#CartDrawer');
    if (!drawer) return cart;
    if (drawer.dataset.freeGiftEnabled !== 'true') return cart;
    const variantId = drawer.dataset.freeGiftVariantId;
    const threshold = parseInt(drawer.dataset.freeGiftThreshold || '0', 10);
    if (!variantId || threshold <= 0) return cart;

    if (!cart) {
      try { cart = await fetchJSON('/cart.js'); } catch { return cart; }
    }

    // Find an existing free-gift line item (matched by property, NOT variant id —
    // so a customer who happens to buy the same product manually keeps theirs).
    const giftItem = (cart.items || []).find(i => {
      const p = i.properties || {};
      return p._free_gift === 'true' || p._free_gift === true;
    });

    // Qualifying BOXES (see lineBoxes), EXCEPT the gift line.
    const qualifyingSubtotal = (cart.items || []).reduce((sum, i) => {
      const p = i.properties || {};
      const isGift = p._free_gift === 'true' || p._free_gift === true;
      return isGift ? sum : sum + lineBoxes(i);
    }, 0);
    const shouldHaveGift = qualifyingSubtotal >= threshold;

    if (shouldHaveGift && !giftItem) {
      _freeGiftSyncing = true;
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('properties[_free_gift]', 'true');
        formData.append('sections', cartState.sectionsToRender().join(','));
        formData.append('sections_url', window.location.pathname);
        const res = await fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const body = await res.json().catch(() => null);
        if (res.ok) {
          // The add response doesn't include cart totals — but it does include
          // sections. Fetch fresh cart, then apply.
          const fresh = await fetchJSON('/cart.js');
          fresh.sections = body && body.sections;
          cartState.applyCart(fresh);
          return fresh;
        } else {
          // Shopify rejected the add — surface the reason. Common causes:
          // product is Draft (not Active), not on the Online Store sales
          // channel, or variant is sold out with continue-selling-when-OOS off.
          console.error('[free gift] Shopify rejected add', {
            variantId,
            status: res.status,
            description: body && (body.description || body.message),
            body
          });
        }
      } catch (err) {
        console.error('[free gift] add failed', err);
      } finally {
        _freeGiftSyncing = false;
      }
    } else if (!shouldHaveGift && giftItem) {
      _freeGiftSyncing = true;
      try {
        const formData = new FormData();
        formData.append('id', giftItem.key);
        formData.append('quantity', '0');
        formData.append('sections', cartState.sectionsToRender().join(','));
        formData.append('sections_url', window.location.pathname);
        const res = await fetch(`${routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const fresh = await res.json().catch(() => null);
        if (res.ok && fresh) {
          cartState.applyCart(fresh);
          return fresh;
        }
      } catch (err) {
        console.error('[free gift] remove failed', err);
      } finally {
        _freeGiftSyncing = false;
      }
    }
    return cart;
  }

  /* Inject "Upgrade to Subscription" button below cart items where the
     product has selling plans and the line item isn't already subscribed.
     We do this in JS because /cart.js doesn't return selling_plan_groups
     and item.product.selling_plan_groups in Liquid has been flaky. */
  const productCache = new Map();
  async function getProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const data = await fetchJSON(`/products/${handle}.js`);
      productCache.set(handle, data);
      return data;
    } catch (e) {
      productCache.set(handle, null);
      return null;
    }
  }
  async function getProductSellingPlans(handle) {
    const product = await getProduct(handle);
    return (product && product.selling_plan_groups) || [];
  }

  /* Gift card amount picker — inject a variant selector for gift card line
     items so the AJAX-refreshed cart drawer shows it without a page reload. */
  async function renderGiftCardPickers(cart) {
    if (!cart) {
      try { cart = await fetchJSON('/cart.js'); } catch { return; }
    }
    const itemsByKey = {};
    (cart.items || []).forEach(i => { itemsByKey[i.key] = i; });

    const elements = $$('[data-cart-item]');
    for (const el of elements) {
      const key = el.dataset.key;
      const cartItem = key && itemsByKey[key];
      if (!cartItem || !cartItem.gift_card) continue;
      // Already has picker? skip
      if (el.querySelector('[data-gift-card-amount]')) continue;

      const titleLink = el.querySelector('.cart-drawer__item-title');
      const href = titleLink && titleLink.getAttribute('href');
      const m = href && href.match(/\/products\/([^?\/#]+)/);
      if (!m) continue;
      const handle = m[1];

      const product = await getProduct(handle);
      if (!product || !product.variants || product.variants.length <= 1) continue;

      const optionsHtml = product.variants.map(v =>
        `<option value="${v.id}"${v.id === cartItem.variant_id ? ' selected' : ''}>${escapeHTML(formatMoney(v.price))}</option>`
      ).join('');

      const wrap = document.createElement('div');
      wrap.className = 'cart-drawer__item-amount';
      wrap.innerHTML = `
        <label class="cart-drawer__item-amount-label" for="GiftAmount-${escapeHTML(key)}">Amount:</label>
        <select id="GiftAmount-${escapeHTML(key)}" class="cart-drawer__item-amount-select" data-gift-card-amount data-key="${escapeHTML(key)}">${optionsHtml}</select>
      `;
      // Insert above the qty + trash row
      const controls = el.querySelector('.cart-drawer__item-controls');
      if (controls && controls.parentElement) {
        controls.parentElement.insertBefore(wrap, controls);
      }
    }
  }
  function discountStringForPlan(plan) {
    const adj = plan && plan.price_adjustments && plan.price_adjustments[0];
    if (!adj) return '';
    if (adj.value_type === 'percentage') return `${adj.value}%`;
    if (adj.value_type === 'fixed_amount') return formatMoney(adj.value);
    return '';
  }
  // Strip discount suffixes from a selling plan name. Handles:
  //   "Deliver every month, 10% off"   → "Deliver every month"
  //   "Deliver every month - 10% off"  → "Deliver every month"
  //   "Deliver every month (10% off)"  → "Deliver every month"
  function cleanPlanName(name) {
    if (!name) return '';
    return String(name)
      .split(',')[0]
      .split(' - ')[0]
      .split(' (')[0]
      .trim();
  }
  function buildUpgradeLabel(plan) {
    const labelTemplate = (window.theme && window.theme.subscriptionUpgradeLabel) || 'Subscribe & Save {discount}';
    const discountStr = discountStringForPlan(plan);
    if (!discountStr) {
      // No discount info — strip a trailing "& Save 15%" if the label has one
      return labelTemplate.replace(/\s*&\s*Save\s+[\d.]+%/i, '').replace('{discount}', '').trim();
    }
    if (labelTemplate.includes('{discount}')) return labelTemplate.replace('{discount}', discountStr);
    // No placeholder but has a literal percentage like "15%" — replace it with the real one
    if (/\d+(\.\d+)?%/.test(labelTemplate)) return labelTemplate.replace(/\d+(\.\d+)?%/, discountStr);
    return labelTemplate;
  }

  async function renderUpgradeButtons(cart) {
    if (!cart) {
      try { cart = await fetchJSON('/cart.js'); } catch { return; }
    }
    const itemsByKey = {};
    (cart.items || []).forEach(i => { itemsByKey[i.key] = i; });

    const elements = $$('[data-cart-item]');

    for (const el of elements) {
      // Free gift lines are bonus add-ons — never offer to subscribe them,
      // never offer to downgrade them. Just leave them as-is.
      if (el.hasAttribute('data-free-gift')) {
        const stale = el.querySelector('[data-upgrade-wrap]');
        if (stale) stale.remove();
        continue;
      }

      const key = el.dataset.key;
      const cartItem = key && itemsByKey[key];
      const existingWrap = el.querySelector('[data-upgrade-wrap]');

      // If item is now a subscription, swap any old upgrade UI for a small
      // "Switch to one-time purchase" downgrade link so users can revert.
      if (cartItem && cartItem.selling_plan_allocation) {
        if (existingWrap) existingWrap.remove();
        const wrap = document.createElement('div');
        wrap.className = 'cart-drawer__upgrade-wrap';
        wrap.dataset.upgradeWrap = '';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cart-drawer__item-upgrade cart-drawer__item-upgrade--downgrade';
        btn.dataset.cartDowngradeSubscription = '';
        btn.dataset.key = cartItem.key;
        if (el.dataset.bundleKeys) btn.dataset.keys = el.dataset.bundleKeys;
        btn.textContent = 'Switch to one-time purchase';
        wrap.appendChild(btn);
        el.appendChild(wrap);
        continue;
      }

      // Item is one-time. Already has the wrap? leave it alone
      if (existingWrap) continue;

      // Look up selling plans for this product (cached)
      const titleLink = el.querySelector('.cart-drawer__item-title');
      const href = titleLink && titleLink.getAttribute('href');
      const m = href && href.match(/\/products\/([^?\/#]+)/);
      if (!m) continue;
      const handle = m[1];

      const groups = await getProductSellingPlans(handle);
      if (!groups.length) continue;
      // Flatten all plans across groups (usually one group)
      const allPlans = groups.reduce((acc, g) => acc.concat(g.selling_plans || []), []);
      if (!allPlans.length || !key) continue;
      const firstPlan = allPlans[0];
      const upgradeLabel = buildUpgradeLabel(firstPlan);

      // Build the upgrade UI inside a wrapper so we can clean it up later
      const wrap = document.createElement('div');
      wrap.className = 'cart-drawer__upgrade-wrap';
      wrap.dataset.upgradeWrap = '';

      if (allPlans.length === 1) {
        // Only one plan — single-click upgrade
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cart-drawer__item-upgrade';
        btn.dataset.cartUpgradeSubscription = '';
        btn.dataset.key = key;
        btn.dataset.plan = firstPlan.id;
        if (el.dataset.bundleKeys) btn.dataset.keys = el.dataset.bundleKeys;
        btn.textContent = upgradeLabel;
        wrap.appendChild(btn);
      } else {
        // Multiple plans — button expands into a frequency picker
        const toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = 'cart-drawer__item-upgrade';
        toggleBtn.dataset.upgradeToggle = '';
        toggleBtn.setAttribute('aria-expanded', 'false');
        toggleBtn.innerHTML = `<span>${escapeHTML(upgradeLabel)}</span><span class="cart-drawer__upgrade-caret" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg></span>`;
        wrap.appendChild(toggleBtn);

        const menu = document.createElement('div');
        menu.className = 'cart-drawer__upgrade-menu';
        menu.dataset.upgradeMenu = '';
        menu.hidden = true;

        const heading = document.createElement('p');
        heading.className = 'cart-drawer__upgrade-menu-heading';
        heading.textContent = 'Choose delivery frequency:';
        menu.appendChild(heading);

        allPlans.forEach(plan => {
          const opt = document.createElement('button');
          opt.type = 'button';
          opt.className = 'cart-drawer__upgrade-option';
          opt.dataset.cartUpgradeSubscription = '';
          opt.dataset.key = key;
          opt.dataset.plan = plan.id;
          if (el.dataset.bundleKeys) opt.dataset.keys = el.dataset.bundleKeys;
          const discountStr = discountStringForPlan(plan);
          opt.innerHTML = `<span class="cart-drawer__upgrade-option-name">${escapeHTML(cleanPlanName(plan.name))}</span>${discountStr ? `<span class="cart-drawer__upgrade-option-discount">Save ${escapeHTML(discountStr)}</span>` : ''}`;
          menu.appendChild(opt);
        });

        wrap.appendChild(menu);
      }

      el.appendChild(wrap);
    }
  }

  /* Mega menu — sidebar categories swap which products panel is visible.
     Hovering a category activates its panel; clicking also works for touch.
     Mobile uses a single "picker" that opens/closes a list of collections;
     selecting one swaps the visible products panel.
     Each products panel is a horizontal scroller with prev/next chevrons. */
  function bindMegaMenu() {
    // Helper: wire prev/next chevrons on a single scroller, show/hide based on overflow.
    function bindScroller(scroller) {
      const track = scroller.querySelector('[data-mega-track]');
      const prev = scroller.querySelector('[data-mega-scroll="prev"]');
      const next = scroller.querySelector('[data-mega-scroll="next"]');
      if (!track || !prev || !next) return;

      function syncButtons() {
        const overflow = track.scrollWidth - track.clientWidth;
        if (overflow <= 2) {
          prev.hidden = true; next.hidden = true; return;
        }
        prev.hidden = false; next.hidden = false;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= overflow - 1;
      }
      function stepBy(dir) {
        const first = track.querySelector('li');
        const step = first ? first.getBoundingClientRect().width + 12 /* gap */ : track.clientWidth;
        track.scrollBy({ left: dir * step * Math.max(1, Math.floor(track.clientWidth / step)), behavior: 'smooth' });
      }
      prev.addEventListener('click', () => stepBy(-1));
      next.addEventListener('click', () => stepBy(1));
      track.addEventListener('scroll', syncButtons, { passive: true });
      // Recheck when fonts/images load (image load can change track width).
      syncButtons();
      if ('ResizeObserver' in window) new ResizeObserver(syncButtons).observe(track);
      scroller._megaSync = syncButtons;
    }

    // Desktop
    $$('[data-mega-menu]').forEach(menu => {
      const cats = $$('.mega-menu__cat', menu);
      const panels = $$('.mega-menu__panel', menu);
      if (!cats.length || !panels.length) return;

      $$('[data-mega-scroller]', menu).forEach(bindScroller);

      function activate(idx) {
        cats.forEach(c => {
          const isActive = c.dataset.megaCat === String(idx);
          c.classList.toggle('is-active', isActive);
          c.setAttribute('aria-expanded', String(isActive));
        });
        panels.forEach(p => {
          const isActive = p.dataset.megaPanel === String(idx);
          p.classList.toggle('is-active', isActive);
          // Re-sync the chevron visibility for the newly-visible scroller
          // (clientWidth is 0 while hidden, so the initial sync was a no-op).
          if (isActive) {
            const sc = p.querySelector('[data-mega-scroller]');
            if (sc && sc._megaSync) sc._megaSync();
          }
        });
      }
      cats.forEach(c => {
        c.addEventListener('mouseenter', () => activate(c.dataset.megaCat));
        c.addEventListener('focus', () => activate(c.dataset.megaCat));
        c.addEventListener('click', (e) => { e.preventDefault(); activate(c.dataset.megaCat); });
      });
    });

    // Mobile picker
    $$('[data-mobile-mega]').forEach(wrap => {
      const toggle = wrap.querySelector('[data-mobile-mega-toggle]');
      const list = wrap.querySelector('[data-mobile-mega-list]');
      const current = wrap.querySelector('[data-mobile-mega-current]');
      const options = $$('[data-mobile-mega-option]', wrap);
      const panels = $$('[data-mobile-mega-panel]', wrap);
      if (!toggle || !list || !options.length) return;

      // Wire each panel's product scroller (mobile uses the same data-mega-* hooks).
      $$('[data-mega-scroller]', wrap).forEach(bindScroller);

      function setOpen(open) {
        list.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
      }
      function pick(idx) {
        options.forEach(o => {
          const sel = o.dataset.mobileMegaOption === String(idx);
          o.classList.toggle('is-selected', sel);
          o.setAttribute('aria-selected', String(sel));
          if (sel && current) current.textContent = o.dataset.name || o.textContent;
        });
        panels.forEach(p => {
          const isActive = p.dataset.mobileMegaPanel === String(idx);
          p.classList.toggle('is-active', isActive);
          // Re-sync the chevron visibility when the panel becomes visible —
          // clientWidth was 0 while display: none.
          if (isActive) {
            const sc = p.querySelector('[data-mega-scroller]');
            if (sc && sc._megaSync) sc._megaSync();
          }
        });
        setOpen(false);
      }
      toggle.addEventListener('click', () => setOpen(list.hidden));
      options.forEach(o => {
        o.addEventListener('click', () => pick(o.dataset.mobileMegaOption));
      });
    });
  }

  function bindStoreLocator() {
    $$('[data-store-locator]').forEach(section => {
      const input = section.querySelector('[data-store-search]');
      const items = $$('[data-store-item]', section);
      const empty = section.querySelector('[data-store-empty]');
      const mapEl = section.querySelector('[data-store-map]');

      // Search filtering
      if (input && items.length) {
        const filter = () => {
          const query = input.value.trim().toLowerCase();
          let visible = 0;
          items.forEach(item => {
            const blob = (item.dataset.storeSearchBlob || '').toLowerCase();
            const match = !query || blob.includes(query);
            item.hidden = !match;
            if (match) visible++;
          });
          if (empty) empty.hidden = visible > 0;
        };
        input.addEventListener('input', debounce(filter, 80));
      }

      // Interactive Leaflet map (only when section has a [data-store-map] container
      // AND at least one store has lat/lng — Liquid only renders the container in
      // that case, so the presence check is sufficient).
      if (!mapEl) return;
      const initMap = () => {
        if (!window.L) return false;
        const itemsWithCoords = items.filter(i => i.dataset.lat && i.dataset.lng);
        if (!itemsWithCoords.length) return true;

        // Lock the map down on mobile: no dragging, no pinch-zoom, no
        // double-click zoom. Only marker taps + the +/- buttons work.
        // This guarantees finger swipes scroll the page and the map can
        // never grow or "pop out" of its container.
        const isMobile = window.matchMedia('(max-width: 990px)').matches
          || (window.L && L.Browser && L.Browser.mobile);
        const map = L.map(mapEl, {
          scrollWheelZoom: false,
          dragging: !isMobile,
          tap: true,
          touchZoom: !isMobile,
          doubleClickZoom: !isMobile,
          boxZoom: false,
          keyboard: false,
        }).setView([0, 0], 2);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap'
        }).addTo(map);

        // Custom pin icon (if uploaded in the theme editor). The pin size
        // setting is the WIDTH — height follows the image's own aspect
        // ratio so wide logos/wordmarks render whole instead of being
        // cropped into a square.
        let customIcon = null;
        const pinUrl = section.dataset.pinUrl;
        const pinIconFor = (w, h) => L.icon({
          iconUrl: pinUrl,
          iconSize: [w, h],
          iconAnchor: [w / 2, h],        // tip at bottom-center
          popupAnchor: [0, -h + 4],      // open popup just above the tip
        });
        if (pinUrl) {
          const pinSize = parseInt(section.dataset.pinSize || '40', 10) || 40;
          // Square placeholder until the real dimensions load.
          customIcon = pinIconFor(pinSize, pinSize);
          const probe = new Image();
          probe.onload = () => {
            if (!probe.naturalWidth || !probe.naturalHeight) return;
            const ratio = probe.naturalHeight / probe.naturalWidth;
            const w = pinSize;
            const h = Math.max(8, Math.round(pinSize * ratio));
            customIcon = pinIconFor(w, h);
            // Update any markers that were already placed with the square guess.
            markers.forEach((m) => m.setIcon(customIcon));
          };
          probe.src = pinUrl;
        }

        const markers = new Map(); // item element → leaflet marker
        const bounds = [];
        itemsWithCoords.forEach(item => {
          const lat = parseFloat(item.dataset.lat);
          const lng = parseFloat(item.dataset.lng);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return;
          const name = item.dataset.storeName || 'Store';
          const address = item.dataset.storeAddress || '';
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
          const popupHtml = `
            <div class="store-locator__popup">
              <strong>${escapeHTML(name)}</strong>
              ${address ? `<p>${escapeHTML(address)}</p>` : ''}
              <a href="${directionsUrl}" target="_blank" rel="noopener">Get directions →</a>
            </div>
          `;
          const markerOpts = customIcon ? { icon: customIcon, keyboard: false } : { keyboard: false };
          const marker = L.marker([lat, lng], markerOpts).addTo(map);
          // Popups can escape the map container on mobile — only enable them
          // on desktop. The store cards already show all the info.
          const isMobileInit = window.matchMedia('(max-width: 990px)').matches
            || (window.L && L.Browser && L.Browser.mobile);
          if (!isMobileInit) marker.bindPopup(popupHtml);
          markers.set(item, marker);
          bounds.push([lat, lng]);
        });

        // Start zoomed out to show the continental US (and all pins within it).
        // Bounds: SW corner (24.5°N, -125°W) → NE corner (49.5°N, -66.9°W).
        const US_BOUNDS = [[24.5, -125], [49.5, -66.9]];
        if (bounds.length === 0) {
          map.fitBounds(US_BOUNDS, { padding: [20, 20] });
        } else {
          map.fitBounds(US_BOUNDS, { padding: [20, 20] });
        }

        // Force Leaflet to recompute its size after initial layout settles.
        const refit = () => {
          map.invalidateSize();
          map.fitBounds(US_BOUNDS, { padding: [20, 20] });
        };
        // Two ticks during initial load only — NOT on every scroll, which
        // was causing layout jumps as the user scrolled.
        requestAnimationFrame(refit);
        setTimeout(refit, 300);
        // Window resize (orientation change) only — debounced
        window.addEventListener('resize', debounce(() => map.invalidateSize(), 150));

        // -------- Geolocation: "Use my location" button --------
        // On grant: pan/zoom to user, drop a marker, sort store list by distance.
        function haversineKm(lat1, lng1, lat2, lng2) {
          const R = 6371;
          const toRad = (d) => d * Math.PI / 180;
          const dLat = toRad(lat2 - lat1);
          const dLng = toRad(lng2 - lng1);
          const a = Math.sin(dLat / 2) ** 2 +
                    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                    Math.sin(dLng / 2) ** 2;
          return 2 * R * Math.asin(Math.sqrt(a));
        }
        const geoBtn = section.querySelector('[data-store-geolocate]');
        if (geoBtn && navigator.geolocation) {
          let userMarker = null;
          geoBtn.addEventListener('click', () => {
            geoBtn.disabled = true;
            const label = geoBtn.querySelector('span');
            const origText = label ? label.textContent : '';
            if (label) label.textContent = 'Locating…';
            navigator.geolocation.getCurrentPosition((pos) => {
              const { latitude: lat, longitude: lng } = pos.coords;
              // Drop a "you are here" marker
              if (userMarker) map.removeLayer(userMarker);
              userMarker = L.circleMarker([lat, lng], {
                radius: 8,
                color: '#FFFFFF',
                weight: 3,
                fillColor: '#3B82F6',
                fillOpacity: 1,
              }).addTo(map);
              userMarker.bindTooltip('You are here', { permanent: false, direction: 'top' });
              // Find nearest stores and sort the list by distance
              const sortable = itemsWithCoords.map(item => ({
                item,
                dist: haversineKm(lat, lng, parseFloat(item.dataset.lat), parseFloat(item.dataset.lng)),
              })).sort((a, b) => a.dist - b.dist);
              // Reorder DOM
              const parent = sortable[0].item.parentElement;
              sortable.forEach(({ item }) => parent.appendChild(item));
              // Pan to user + nearest store
              const nearest = sortable[0];
              const targetBounds = [
                [lat, lng],
                [parseFloat(nearest.item.dataset.lat), parseFloat(nearest.item.dataset.lng)],
              ];
              map.fitBounds(targetBounds, { padding: [60, 60], maxZoom: 12 });
              if (label) label.textContent = origText;
              geoBtn.disabled = false;
            }, (err) => {
              if (label) label.textContent = 'Location unavailable';
              setTimeout(() => {
                if (label) label.textContent = origText;
                geoBtn.disabled = false;
              }, 2000);
            }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 });
          });
        }

        // Clicking a store card pans/zooms the map to its marker and opens the popup.
        // On mobile, also scroll the map into view so the user can see the result.
        itemsWithCoords.forEach(item => {
          const marker = markers.get(item);
          if (!marker) return;
          item.style.cursor = 'pointer';
          item.addEventListener('click', (e) => {
            // Don't hijack clicks on actual links (Get directions, phone)
            if (e.target.closest('a, button')) return;

            // Mark this card selected (clear the others)
            items.forEach((other) => other.classList.remove('is-active'));
            item.classList.add('is-active');

            const isMobile = window.matchMedia('(max-width: 990px)').matches;
            const targetZoom = Math.max(map.getZoom(), 14);

            if (isMobile) {
              // Mobile: scroll the map into view, then zoom. NO popup
              // (popups can escape the rounded container). The card itself
              // already shows the store details.
              mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
              setTimeout(() => {
                map.invalidateSize();
                map.setView(marker.getLatLng(), targetZoom, { animate: true, duration: 0.6 });
              }, 450);
            } else {
              map.flyTo(marker.getLatLng(), targetZoom, { duration: 0.6 });
              marker.openPopup();
            }
          });
        });
        return true;
      };

      // Leaflet is loaded via <script defer>; wait for it
      if (window.L) {
        initMap();
      } else {
        const interval = setInterval(() => {
          if (window.L && initMap()) clearInterval(interval);
        }, 100);
        setTimeout(() => clearInterval(interval), 8000);
      }
    });
  }

  function bindTestimonialsCarousel() {
    $$('[data-testimonials-carousel]').forEach(carousel => {
      if (carousel.dataset.layout !== 'scroll') return;
      const track = carousel.querySelector('[data-t-track]');
      const prev = carousel.querySelector('[data-t-prev]');
      const next = carousel.querySelector('[data-t-next]');
      if (!track || !prev || !next) return;

      const slideWidth = () => {
        const slide = track.querySelector('.testimonial');
        if (!slide) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0);
        return slide.getBoundingClientRect().width + gap;
      };
      const updateButtons = () => {
        const max = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= max;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -slideWidth(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left:  slideWidth(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(updateButtons, 80), { passive: true });
      window.addEventListener('resize', debounce(updateButtons, 100));
      updateButtons();
    });
  }

  function bindVideoSlider() {
    $$('[data-video-slider]').forEach(section => {
      const track = section.querySelector('[data-vs-track]');
      const prev = section.querySelector('[data-vs-prev]');
      const next = section.querySelector('[data-vs-next]');
      const modal = section.querySelector('[data-vs-modal]');
      const modalVideo = section.querySelector('[data-vs-video]');

      // Section-wide audio preference: once user unmutes any video, all subsequent plays are unmuted.
      // Stored on the section element so it survives across calls.
      const getAudioOn = () => section.dataset.vsAudioOn === '1';
      const setAudioOn = (on) => {
        section.dataset.vsAudioOn = on ? '1' : '0';
        // Sync every video and every mute button in this section
        $$('.video-slider__inline-video[data-vs-inline]', section).forEach(v => { v.muted = !on; });
        $$('[data-vs-feed-video]', section).forEach(v => { v.muted = !on; });
        $$('[data-vs-mute]', section).forEach(b => b.setAttribute('data-muted', on ? 'false' : 'true'));
        $$('[data-vs-feed-mute]', section).forEach(b => b.setAttribute('data-muted', on ? 'false' : 'true'));
      };

      // Inline click-to-play: click any card → play in place, pause every other card.
      // Set up AFTER the carousel finishes cloning slides so clones are wired up too.
      const setupInlineAutoplay = () => {
        const inlineVideos = () => $$('.video-slider__inline-video[data-vs-inline]', section);
        const pauseAll = (except) => {
          inlineVideos().forEach(v => {
            if (v === except) return;
            try { v.pause(); } catch (_) {}
            const item = v.closest('.video-slider__item');
            if (item) item.removeAttribute('data-vs-playing');
          });
        };
        const playInline = (v) => {
          const src = v.dataset.videoSrc;
          if (!src) return;
          if (!v.src) {
            v.src = src;
            v.load();
          }
          v.muted = !getAudioOn();
          // Mirror the chosen video to every clone of this slide so clones reflect playback state
          pauseAll(v);
          v.play().then(() => {
            const item = v.closest('.video-slider__item');
            if (item) item.setAttribute('data-vs-playing', '');
          }).catch(() => {
            // If unmuted play was blocked, retry muted so it still plays
            if (!v.muted) {
              v.muted = true;
              v.play().then(() => {
                const item = v.closest('.video-slider__item');
                if (item) item.setAttribute('data-vs-playing', '');
              }).catch(() => {});
            }
          });
        };
        const pauseInline = (v) => {
          try { v.pause(); } catch (_) {}
          const item = v.closest('.video-slider__item');
          if (item) item.removeAttribute('data-vs-playing');
        };

        // Mute toggle buttons (desktop cards + feed slides) — share the same section-wide audio flag
        $$('[data-vs-mute], [data-vs-feed-mute]', section).forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            setAudioOn(!getAudioOn());
          });
        });

        const isMobileViewport = () => window.matchMedia('(max-width: 749px)').matches;

        // Open the fullscreen TikTok-style feed from a clicked card (mobile only)
        const openFeedFromCard = (card) => {
          const feed = section.querySelector('[data-vs-feed]');
          const feedTrack = section.querySelector('[data-vs-feed-track]');
          const feedSlides = $$('[data-vs-feed-slide]', section);
          if (!feed || !feedTrack || !feedSlides.length) return false;

          // Match the clicked card to a feed slide by its video src
          const clickedSrc = card.dataset.videoSrc;
          let startIdx = 0;
          for (let i = 0; i < feedSlides.length; i++) {
            const source = feedSlides[i].querySelector('source');
            if (source && source.getAttribute('src') === clickedSrc) {
              startIdx = i;
              break;
            }
          }

          // Hand off cleanly: pause every inline card video first
          inlineVideos().forEach(iv => {
            try { iv.pause(); } catch (_) {}
            const item = iv.closest('.video-slider__item');
            if (item) item.removeAttribute('data-vs-playing');
          });

          feed.hidden = false;
          document.documentElement.style.overflow = 'hidden';
          requestAnimationFrame(() => {
            const targetSlide = feedSlides[startIdx];
            if (targetSlide) feedTrack.scrollTop = targetSlide.offsetTop;
            feedSlides.forEach((s, i) => {
              s.removeAttribute('data-paused');
              const v = s.querySelector('[data-vs-feed-video]');
              if (!v) return;
              if (i === startIdx) {
                // First tap is a user gesture, so we can try unmuted. Reflect that in the audio flag
                // unless the user explicitly muted previously.
                v.muted = !getAudioOn() && section.dataset.vsAudioOn !== '0';
                if (section.dataset.vsAudioOn === undefined) {
                  // First-ever play in this session: default to sound on
                  setAudioOn(true);
                  v.muted = false;
                }
                v.play().catch(() => {
                  v.muted = true;
                  setAudioOn(false);
                  v.play().catch(() => {});
                });
              } else {
                try { v.pause(); } catch (_) {}
              }
            });
          });
          return true;
        };

        // Click any card → desktop: toggle inline play. Mobile: open fullscreen feed.
        section.addEventListener('click', (e) => {
          if (e.target.closest('.video-slider__product')) return;
          if (e.target.closest('[data-vs-feed]')) return;
          if (e.target.closest('[data-vs-modal]')) return;
          const card = e.target.closest('[data-vs-open]');
          if (!card) return;
          e.preventDefault();
          e.stopPropagation();
          if (isMobileViewport()) {
            openFeedFromCard(card);
            return;
          }
          const item = card.closest('.video-slider__item');
          if (!item) return;
          const v = item.querySelector('.video-slider__inline-video[data-vs-inline]');
          if (!v) return;
          if (v.paused) {
            playInline(v);
          } else {
            pauseInline(v);
          }
        }, true); // capture phase so we beat the modal/feed handler below

        // When any inline video ends, drop the playing flag so the play icon comes back
        section.addEventListener('ended', (e) => {
          if (e.target.matches?.('.video-slider__inline-video')) {
            const item = e.target.closest('.video-slider__item');
            if (item) item.removeAttribute('data-vs-playing');
          }
        }, true);

        // Pause everything when the tab is hidden so we don't burn battery
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) inlineVideos().forEach(pauseInline);
        });
      };

      // ----- Infinite-loop carousel (same pattern as featured-collection) -----
      if (track) {
        const originalSlides = $$('.video-slider__item', track);
        if (originalSlides.length >= 2) {
          // Seed: clone the full set on each side so users can scroll either way
          // Clones are aria-hidden, so anything focusable inside must drop
          // out of the tab order or screen readers flag the contradiction.
          const defocus = (c) => {
            c.querySelectorAll('a, button, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
            return c;
          };
          const cloneSet = (side) => {
            if (side === 'left') {
              for (let i = originalSlides.length - 1; i >= 0; i--) {
                const c = defocus(originalSlides[i].cloneNode(true));
                c.dataset.vsClone = 'left';
                c.setAttribute('aria-hidden', 'true');
                track.prepend(c);
              }
            } else {
              originalSlides.forEach(s => {
                const c = defocus(s.cloneNode(true));
                c.dataset.vsClone = 'right';
                c.setAttribute('aria-hidden', 'true');
                track.appendChild(c);
              });
            }
          };
          cloneSet('left');
          cloneSet('right');

          const slideWidth = () => {
            const item = track.querySelector('.video-slider__item');
            if (!item) return track.clientWidth;
            const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0);
            return item.getBoundingClientRect().width + gap;
          };

          // Land on the first real slide on load (skip past the prepended clones)
          requestAnimationFrame(() => {
            track.scrollLeft = originalSlides.length * slideWidth();
          });

          // Grow the track on demand so the user never hits an end
          let expandScheduled = false;
          const expandIfNeeded = () => {
            expandScheduled = false;
            const buffer = slideWidth() * 4;
            if (track.scrollWidth - track.scrollLeft - track.clientWidth < buffer) {
              cloneSet('right');
            }
            if (track.scrollLeft < buffer) {
              const beforeWidth = track.scrollWidth;
              cloneSet('left');
              track.scrollLeft += (track.scrollWidth - beforeWidth);
            }
          };
          track.addEventListener('scroll', () => {
            if (expandScheduled) return;
            expandScheduled = true;
            requestAnimationFrame(expandIfNeeded);
          }, { passive: true });

          prev?.addEventListener('click', () => track.scrollBy({ left: -slideWidth(), behavior: 'smooth' }));
          next?.addEventListener('click', () => track.scrollBy({ left:  slideWidth(), behavior: 'smooth' }));
        } else {
          // Fewer than 2 slides: just do simple click-to-scroll, no clones needed
          const slideWidth = () => {
            const item = track.querySelector('.video-slider__item');
            return item ? item.getBoundingClientRect().width : track.clientWidth;
          };
          prev?.addEventListener('click', () => track.scrollBy({ left: -slideWidth(), behavior: 'smooth' }));
          next?.addEventListener('click', () => track.scrollBy({ left:  slideWidth(), behavior: 'smooth' }));
        }
      }

      // ----- Modal play with prev/next navigation -----
      if (modal && modalVideo) {
        // Collect ALL cards that can open the modal, but de-dupe clones so
        // navigation cycles through unique videos only.
        const allOpenCards = () => $$('[data-vs-open]', section);
        const originalCards = () => allOpenCards().filter(c => !c.closest('[data-vs-clone]'));
        let currentIndex = 0;

        // Mobile fullscreen TikTok-style feed
        const feed = section.querySelector('[data-vs-feed]');
        const feedTrack = section.querySelector('[data-vs-feed-track]');
        const feedSlides = $$('[data-vs-feed-slide]', section);
        const feedClose = section.querySelector('[data-vs-feed-close]');
        const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

        const pauseAllFeedVideos = () => {
          feedSlides.forEach(s => {
            const v = s.querySelector('[data-vs-feed-video]');
            if (v) { try { v.pause(); } catch (_) {} }
          });
        };

        const openFeed = (clickedCard) => {
          if (!feed || !feedTrack) return false;
          const list = originalCards();
          const clickedSrc = clickedCard.dataset.videoSrc;
          const startIdx = Math.max(0, list.findIndex(c => c.dataset.videoSrc === clickedSrc));
          feed.hidden = false;
          document.documentElement.style.overflow = 'hidden';
          // Jump to the matching slide before paint so the user lands on it
          requestAnimationFrame(() => {
            const targetSlide = feedSlides[startIdx];
            if (targetSlide) {
              feedTrack.scrollTop = targetSlide.offsetTop;
            }
            // Kick off playback on the active slide (IO will handle the rest as the user scrolls)
            feedSlides.forEach((s, i) => {
              s.removeAttribute('data-paused');
              const v = s.querySelector('[data-vs-feed-video]');
              if (!v) return;
              if (i === startIdx) {
                v.muted = true; // autoplay requires muted on mobile
                v.play().catch(() => {});
              } else {
                try { v.pause(); } catch (_) {}
              }
            });
          });
          return true;
        };

        const closeFeed = () => {
          if (!feed) return;
          feed.hidden = true;
          document.documentElement.style.overflow = '';
          pauseAllFeedVideos();
        };

        feedClose?.addEventListener('click', closeFeed);

        // Autoplay the slide that's centered; pause the rest as user scrolls
        if (feedTrack && feedSlides.length && 'IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              const slide = entry.target;
              const v = slide.querySelector('[data-vs-feed-video]');
              if (!v) return;
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                if (!slide.hasAttribute('data-paused') && !feed.hidden) {
                  v.muted = section.dataset.vsAudioOn !== '1';
                  v.play().catch(() => {
                    v.muted = true;
                    v.play().catch(() => {});
                  });
                }
              } else {
                try { v.pause(); } catch (_) {}
              }
            });
          }, { root: feedTrack, threshold: [0, 0.6, 1] });
          feedSlides.forEach(s => io.observe(s));
        }

        // Tap a slide to toggle play/pause + show/hide the play overlay
        feedSlides.forEach(slide => {
          const tap = slide.querySelector('[data-vs-feed-tap]');
          const v = slide.querySelector('[data-vs-feed-video]');
          if (tap && v) {
            tap.addEventListener('click', (e) => {
              e.preventDefault();
              if (v.paused) {
                v.muted = section.dataset.vsAudioOn !== '1';
                v.play().catch(() => {
                  v.muted = true;
                  v.play().catch(() => {});
                });
                slide.removeAttribute('data-paused');
              } else {
                v.pause();
                slide.setAttribute('data-paused', 'true');
              }
            });
          }

          // Share button → Web Share API on mobile, fallback to copy-link
          const shareBtn = slide.querySelector('[data-vs-feed-share]');
          if (shareBtn) {
            shareBtn.addEventListener('click', async (e) => {
              e.preventDefault();
              e.stopPropagation();
              const url = shareBtn.dataset.shareUrl || window.location.href;
              const title = shareBtn.dataset.shareTitle || document.title;
              try {
                if (navigator.share) {
                  await navigator.share({ title, url });
                } else if (navigator.clipboard) {
                  await navigator.clipboard.writeText(url);
                  // Tiny inline confirmation
                  const label = shareBtn.querySelector('.video-slider__feed-share-label');
                  if (label) {
                    const original = label.textContent;
                    label.textContent = 'Copied!';
                    setTimeout(() => { label.textContent = original; }, 1500);
                  }
                }
              } catch (_) { /* user cancelled / unavailable */ }
            });
          }
        });

        // Esc closes the feed too
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && feed && !feed.hidden) {
            closeFeed();
          }
        });

        const loadIndex = (idx) => {
          const list = originalCards();
          if (!list.length) return;
          // Wrap around: -1 → last, length → 0
          if (idx < 0) idx = list.length - 1;
          if (idx >= list.length) idx = 0;
          currentIndex = idx;
          const card = list[idx];
          const src = card.dataset.videoSrc;
          const poster = card.dataset.videoPoster;
          if (!src) return;
          modalVideo.pause();
          modalVideo.src = src;
          if (poster) modalVideo.setAttribute('poster', poster);
          modalVideo.load();
          modalVideo.play().catch(() => {});
        };

        const open = (clickedCard) => {
          const list = originalCards();
          const clickedSrc = clickedCard.dataset.videoSrc;
          const startIdx = Math.max(0, list.findIndex(c => c.dataset.videoSrc === clickedSrc));
          modal.hidden = false;
          document.documentElement.style.overflow = 'hidden';
          loadIndex(startIdx);
        };
        const close = () => {
          modal.hidden = true;
          modalVideo.pause();
          modalVideo.removeAttribute('src');
          modalVideo.load();
          document.documentElement.style.overflow = '';
        };

        // NOTE: Card clicks are handled by the inline click-to-play handler above
        // (registered in setupInlineAutoplay). We no longer open the modal or
        // the fullscreen feed on card click — videos play in-place on the card.
        $$('[data-vs-close]', section).forEach(el => el.addEventListener('click', close));

        // Prev/next buttons inside the modal
        const prevBtn = section.querySelector('[data-vs-modal-prev]');
        const nextBtn = section.querySelector('[data-vs-modal-next]');
        prevBtn?.addEventListener('click', () => loadIndex(currentIndex - 1));
        nextBtn?.addEventListener('click', () => loadIndex(currentIndex + 1));

        // Keyboard: arrows navigate, Esc closes
        document.addEventListener('keydown', (e) => {
          if (modal.hidden) return;
          if (e.key === 'Escape') { close(); return; }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); loadIndex(currentIndex - 1); }
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); loadIndex(currentIndex + 1); }
        });

        // Touch swipe (vertical) on the modal content
        let touchStartY = 0;
        let touchStartX = 0;
        modal.addEventListener('touchstart', (e) => {
          if (!e.touches[0]) return;
          touchStartY = e.touches[0].clientY;
          touchStartX = e.touches[0].clientX;
        }, { passive: true });
        modal.addEventListener('touchend', (e) => {
          if (!e.changedTouches[0]) return;
          const dy = e.changedTouches[0].clientY - touchStartY;
          const dx = e.changedTouches[0].clientX - touchStartX;
          // Require a clear vertical (or horizontal) gesture > 60px
          if (Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx)) {
            loadIndex(dy < 0 ? currentIndex + 1 : currentIndex - 1);
          } else if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
            loadIndex(dx < 0 ? currentIndex + 1 : currentIndex - 1);
          }
        }, { passive: true });
      }

      // Wire up inline autoplay last so any cloned slides are observed too
      setupInlineAutoplay();
    });
  }

  function bindInfluencerGiftForm() {
    $$('[data-igf]').forEach(section => {
      const form = section.querySelector('[data-igf-form]');
      const thanks = section.querySelector('[data-igf-thanks]');
      const submitBtn = section.querySelector('[data-igf-submit]');
      const submitLabel = section.querySelector('[data-igf-submit-label]');
      const errorEl = section.querySelector('[data-igf-error]');
      if (!form || !submitBtn) return;

      const endpoint = section.dataset.submitEndpoint;
      const maxPerPerson = parseInt(section.dataset.maxPerPerson, 10) || 3;
      const MAX_PER_PRODUCT = 3; // tap 1 → 1, tap 2 → 2, tap 3 → 3, tap 4 → 0
      const countEl = section.querySelector('[data-igf-count]');
      const productCards = $$('[data-igf-product]', section);
      const selected = new Map(); // product GID → quantity (1 or 2)

      const totalSelectedQty = () => {
        let n = 0;
        selected.forEach((qty) => { n += qty; });
        return n;
      };

      const refreshSelectionUi = () => {
        const total = totalSelectedQty();
        if (countEl) countEl.textContent = String(total);
        const atLimit = total >= maxPerPerson;
        productCards.forEach(card => {
          const gid = card.dataset.productGid;
          const qty = selected.get(gid) || 0;
          card.setAttribute('data-qty', String(qty));
          card.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false');
          // Lock the card from going UP if we've hit the cap and this card isn't already at 2
          const canIncrement = (qty < MAX_PER_PRODUCT) && (total < maxPerPerson);
          // We still allow clicking to wrap from 2 → 0 (decrement), so only lock cards at qty 0 (or 1 if no headroom)
          if (atLimit && qty === 0) {
            card.setAttribute('data-locked', 'true');
          } else if (!canIncrement && qty === 1) {
            // qty 1 + at limit → tapping would normally go to 2 but no headroom; instead it wraps to 0
            card.removeAttribute('data-locked');
          } else {
            card.removeAttribute('data-locked');
          }
          // Update the CTA "Added" label to show quantity when > 1
          const ctaText = card.querySelector('[data-igf-cta-text]');
          if (ctaText) ctaText.textContent = qty > 1 ? `Added × ${qty}` : 'Added';
          // Update qty badge text (only visible when qty > 0 via CSS)
          const badge = card.querySelector('[data-igf-qty-badge]');
          if (badge) badge.textContent = String(qty);
        });
      };

      productCards.forEach(card => {
        card.addEventListener('click', () => {
          const gid = card.dataset.productGid;
          if (!gid) return;
          const current = selected.get(gid) || 0;
          const total = totalSelectedQty();

          let next;
          if (current === 0) {
            if (total >= maxPerPerson) return; // locked at cap
            next = 1;
          } else if (current < MAX_PER_PRODUCT) {
            // Try to bump by 1; if that would exceed the per-person cap, wrap to 0.
            next = (total < maxPerPerson) ? current + 1 : 0;
          } else { // already at MAX_PER_PRODUCT → wrap to 0
            next = 0;
          }

          if (next === 0) selected.delete(gid);
          else selected.set(gid, next);
          refreshSelectionUi();
        });
      });

      const showError = (msg) => {
        if (!errorEl) return;
        errorEl.textContent = msg;
        errorEl.hidden = false;
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
      const clearError = () => {
        if (!errorEl) return;
        errorEl.hidden = true;
        errorEl.textContent = '';
      };

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearError();

        if (!endpoint) {
          showError('Form is missing its submit URL. Set it in section settings.');
          return;
        }
        if (selected.size === 0) {
          showError('Pick at least one product you’d like us to send.');
          return;
        }
        // Flatten Map<gid, qty> into a list with duplicates so the Worker can build line items
        const productGidsWithQty = [];
        selected.forEach((qty, gid) => {
          for (let i = 0; i < qty; i++) productGidsWithQty.push(gid);
        });

        const fd = new FormData(form);
        // Normalize a handle: strip URL prefixes, leading @, and trailing slashes
        const normalizeHandle = (raw) => {
          let v = String(raw || '').trim();
          if (!v) return '';
          v = v.replace(/^https?:\/\/(www\.)?(instagram|tiktok)\.com\//i, '');
          v = v.replace(/^@/, '').replace(/\/$/, '');
          return v ? '@' + v : '';
        };

        const payload = {
          firstName: String(fd.get('firstName') || '').trim(),
          lastName: String(fd.get('lastName') || '').trim(),
          email: String(fd.get('email') || '').trim(),
          instagramHandle: normalizeHandle(fd.get('instagramHandle')),
          tiktokHandle: normalizeHandle(fd.get('tiktokHandle')),
          addressLine1: String(fd.get('addressLine1') || '').trim(),
          addressLine2: String(fd.get('addressLine2') || '').trim(),
          city: String(fd.get('city') || '').trim(),
          state: String(fd.get('state') || '').trim(),
          zip: String(fd.get('zip') || '').trim(),
          country: String(fd.get('country') || 'United States').trim(),
          productGids: productGidsWithQty,
          campaign: String(section.dataset.campaign || '').trim(),
          campaignGid: String(section.dataset.campaignGid || '').trim(),
        };

        // Client-side check: at least one social handle
        if (!payload.instagramHandle && !payload.tiktokHandle) {
          showError('Add at least one social handle (Instagram or TikTok).');
          return;
        }

        submitBtn.disabled = true;
        submitBtn.dataset.loading = 'true';
        if (submitLabel) submitLabel.textContent = 'Submitting…';

        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await res.json().catch(() => ({}));

          if (res.ok && data && data.ok) {
            // Success: swap form for thank-you state
            form.hidden = true;
            if (thanks) thanks.hidden = false;
            // Scroll the thank-you state into view
            (thanks || form).scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }

          // Surface validation errors / duplicate-email rejection
          const formatErr = (e) => {
            if (!e) return '';
            if (typeof e === 'string') return e;
            if (Array.isArray(e)) return e.map(formatErr).filter(Boolean).join(' ');
            if (typeof e === 'object') {
              if (e.message) return String(e.message);
              if (e.field && e.code) return `${e.field}: ${e.code}`;
              try { return JSON.stringify(e); } catch (_) { return ''; }
            }
            return String(e);
          };

          let msg = '';
          if (data && Array.isArray(data.errors) && data.errors.length) {
            msg = data.errors.map(formatErr).filter(Boolean).join(' ');
          } else if (data && data.error) {
            msg = formatErr(data.error);
          }
          // Always log the raw response so we can see the underlying GraphQL error in the console
          try { console.error('Gift form submit error:', data); } catch (_) {}
          showError(msg || 'Something went wrong. Please try again.');
        } catch (err) {
          showError('Network error — check your connection and try again.');
        } finally {
          submitBtn.disabled = false;
          submitBtn.dataset.loading = 'false';
          if (submitLabel) submitLabel.textContent = 'Submit';
        }
      });
    });
  }

  function bindFaqTabbed() {
    $$('[data-faq-tabbed]').forEach(section => {
      const links = $$('[data-faq-nav-link]', section);
      const categories = $$('[data-faq-category]', section);
      if (!links.length || !categories.length) return;

      // Click → smooth scroll to category
      links.forEach(link => {
        link.addEventListener('click', (e) => {
          const target = section.querySelector('#' + link.dataset.target);
          if (!target) return;
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Also fix the hash without jumping
          history.replaceState(null, '', '#' + link.dataset.target);
        });
      });

      // Scroll → update active link based on which category is in view
      if (!('IntersectionObserver' in window)) return;
      const linkByTarget = new Map(links.map(l => [l.dataset.target, l]));
      let activeId = null;
      const setActive = (id) => {
        if (id === activeId) return;
        activeId = id;
        links.forEach(l => l.classList.toggle('is-active', l.dataset.target === id));
      };
      // Use a tall negative bottom margin so the "active" category is the one nearest the top of the viewport
      const observer = new IntersectionObserver((entries) => {
        // Pick the entry that's most prominently in the top portion of the viewport
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.id;
          if (linkByTarget.has(id)) setActive(id);
        }
      }, { rootMargin: '-20% 0px -70% 0px', threshold: 0 });
      categories.forEach(c => { if (c.id) observer.observe(c); });
    });
  }

  function bindQuickAddCarousel() {
    const carousel = $('[data-qa-carousel]');
    if (!carousel) return;
    const track = carousel.querySelector('[data-qa-track]');
    const prev = carousel.querySelector('[data-qa-prev]');
    const next = carousel.querySelector('[data-qa-next]');
    if (!track) return;

    const slideWidth = () => {
      const item = track.querySelector('.cart-drawer__quick-add-item');
      if (!item) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0);
      return item.getBoundingClientRect().width + gap;
    };
    const updateButtons = () => {
      const max = track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.disabled = track.scrollLeft <= 0;
      if (next) next.disabled = track.scrollLeft >= max;
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -slideWidth(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left:  slideWidth(), behavior: 'smooth' }));
    track.addEventListener('scroll', debounce(updateButtons, 80), { passive: true });
    updateButtons();
  }

  /* ---------- Promo code (applies via /checkout?discount=) ---------- */
  const promoCode = {
    KEY: 'cart_discount_code',
    get() { return sessionStorage.getItem(this.KEY) || ''; },
    set(code) {
      if (code) sessionStorage.setItem(this.KEY, code);
      else sessionStorage.removeItem(this.KEY);
      this.sync();
    },
    sync() {
      const code = this.get();
      const form = document.getElementById('cart-drawer-form');
      if (form) {
        form.action = code
          ? `/checkout?discount=${encodeURIComponent(code)}`
          : (routes.cart_url || '/cart');
      }
      const input = $('[data-discount-input]');
      const status = $('[data-discount-status]');
      const statusText = $('[data-discount-status-text]');
      const row = $('.cart-drawer__promo-row');
      if (input) input.value = code;
      if (status) status.hidden = !code;
      if (row) row.style.display = code ? 'none' : '';
      if (statusText && code) statusText.textContent = `Code "${code}" will be applied at checkout`;
    },
    init() {
      this.sync();
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-discount-apply]')) {
          const input = $('[data-discount-input]');
          const code = (input?.value || '').trim();
          if (code) this.set(code);
        }
        if (e.target.closest('[data-discount-remove]')) {
          this.set('');
          const details = $('.cart-drawer__promo');
          if (details) details.open = true;
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) {
          e.preventDefault();
          const code = (e.target.value || '').trim();
          if (code) this.set(code);
        }
      });
    }
  };

  /* ---------- Cart state + UI updates ---------- */
  const cartState = {
    update(cart) {
      // Header count
      $$('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.dataset.cartCount = cart.item_count;
      });
      // Subtotals
      $$('[data-cart-subtotal]').forEach(el => { el.textContent = formatMoney(cart.total_price); });
    },

    async refresh() {
      const cart = await fetchJSON(`${routes.cart_url}.js`);
      this.update(cart);
      return cart;
    },

    async refreshDrawer() {
      try {
        // Always fetch fresh cart state for header counts, shipping bar, etc.
        const cart = await fetchJSON(`${routes.cart_url}.js?_=${Date.now()}`);

        // Re-render the cart-drawer section via Shopify's Section Rendering API
        // so we get the full Liquid output (subscription indicators, upgrade
        // buttons, anything else that depends on data not in /cart.js).
        let bodyUpdated = false;
        try {
          const res = await fetch(`${routes.cart_url}?sections=cart-drawer&_=${Date.now()}`, {
            headers: { 'Accept': 'application/json' },
            cache: 'no-store'
          });
          if (res.ok) {
            const data = await res.json();
            const html = data['cart-drawer'];
            if (html) {
              const tmp = document.createElement('div');
              tmp.innerHTML = html;
              const newBody = tmp.querySelector('[data-cart-drawer-body]');
              const liveBody = $('[data-cart-drawer-body]');
              if (newBody && liveBody) {
                liveBody.innerHTML = newBody.innerHTML;
                bodyUpdated = true;
              }
            }
          }
        } catch (sectionErr) {
          console.warn('[cart] section-render failed, falling back to JS render', sectionErr);
        }

        // Fallback: rebuild from /cart.js (no upgrade button, no plan name)
        if (!bodyUpdated) {
          const liveBody = $('[data-cart-drawer-body]');
          if (liveBody) liveBody.innerHTML = renderCartDrawerHTML(cart);
        }

        toggleCartDrawerChrome(cart);
        promoCode.sync();
        this.update(cart);
        updateShippingProgress(cart);
        updateGiftProgress(cart);
        renderUpgradeButtons(cart);
        renderGiftCardPickers(cart);
        return cart;
      } catch (err) {
        console.error('[cart] refreshDrawer failed', err);
      }
    },

    async refreshCartPage() {
      // If we're on the cart page, refresh the table so removed items disappear
      if (!$('.cart-page')) return;
      try {
        const res = await fetch(`${routes.cart_url}?sections=main-cart&_=${Date.now()}`, {
          headers: { 'Accept': 'application/json' },
          cache: 'no-store'
        });
        if (res.ok) {
          const data = await res.json();
          const html = data['main-cart'];
          if (html) {
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            const newSection = tmp.querySelector('.cart-page');
            const oldSection = $('.cart-page');
            if (newSection && oldSection) {
              oldSection.innerHTML = newSection.innerHTML;
              return;
            }
          }
        }
      } catch (err) {
        console.warn('[cart] cart-page section render failed, reloading', err);
      }
      // Fallback: full page reload
      window.location.reload();
    },

    // Which sections to ask Shopify to render alongside a cart mutation.
    // Bundling them into the change/add request means ONE network round trip
    // instead of separate /cart.js + /cart?sections= fetches.
    sectionsToRender() {
      const list = ['cart-drawer'];
      if ($('.cart-page')) list.push('main-cart');
      return list;
    },

    // Apply a cart response that includes a `sections` map (rendered HTML).
    // Falls back to a JS re-render if sections are missing.
    applyCart(cart) {
      this.cart = cart;
      syncMinQtyInputs(cart);
      const sections = cart && cart.sections;

      // Cart drawer body
      const drawerHTML = sections && sections['cart-drawer'];
      const liveBody = $('[data-cart-drawer-body]');
      if (drawerHTML && liveBody) {
        const tmp = document.createElement('div');
        tmp.innerHTML = drawerHTML;
        const newBody = tmp.querySelector('[data-cart-drawer-body]');
        if (newBody) liveBody.innerHTML = newBody.innerHTML;
      } else if (liveBody && cart && cart.items) {
        liveBody.innerHTML = renderCartDrawerHTML(cart);
      }

      // Cart page (only when present)
      const cartHTML = sections && sections['main-cart'];
      const oldSection = $('.cart-page');
      if (cartHTML && oldSection) {
        const tmp = document.createElement('div');
        tmp.innerHTML = cartHTML;
        const newSection = tmp.querySelector('.cart-page');
        if (newSection) oldSection.innerHTML = newSection.innerHTML;
      }

      // Post-render enhancements (all take the cart we already have — no re-fetch)
      toggleCartDrawerChrome(cart);
      promoCode.sync();
      this.update(cart);
      updateShippingProgress(cart);
      updateGiftProgress(cart);
      renderUpgradeButtons(cart);
      renderGiftCardPickers(cart);
      // A cart under the order minimum (an add from anywhere, an old cart, a
      // removed line) tops up its first single-box line to reach it; the free
      // gift then syncs on the cart that comes back. Bundle parts (the Variety
      // Pack's one-of-each) are never stepped on their own.
      const boxes = cartBoxes(cart);
      const minBoxes = minCartBoxes();
      if (boxes > 0 && boxes < minBoxes) {
        const short = (cart.items || []).find(i => !i.properties?._bundle && !isFreeGiftLine(i) && lineBoxes({ ...i, quantity: 1 }) === 1);
        if (short) {
          this.changeItem(short.key, short.quantity + minBoxes - boxes).catch(err => console.error('[cart] minimum quantity failed', err));
          return;
        }
      }
      // Free gift: auto-add or auto-remove based on the new cart state. The
      // function itself guards against re-entry, so chaining is safe.
      syncFreeGift(cart);
    },

    async addItem(form) {
      const formData = new FormData(form);
      const components = bundleComponentsFor(formData.get('id'));
      let res;
      if (components) {
        // Bundle product: add its components instead (Theme settings → Cart →
        // Bundles). The warehouse then sees the real SKUs. The selected
        // selling plan and any line properties carry over to every component.
        const qty = parseInt(formData.get('quantity'), 10) || 1;
        const plan = formData.get('selling_plan');
        const props = {};
        for (const [k, v] of formData.entries()) {
          const m = k.match(/^properties\[(.+)\]$/);
          if (m) props[m[1]] = v;
        }
        const titleEl = form.closest('[data-product-title]') || form.querySelector('[data-product-title]');
        props._bundle = form.dataset.productTitle || (titleEl && titleEl.dataset.productTitle) || 'Bundle';
        const items = components.map(c => {
          const item = { id: c.id, quantity: c.qty * qty, properties: props };
          if (plan) item.selling_plan = plan;
          return item;
        });
        res = await fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: this.sectionsToRender().join(','), sections_url: window.location.pathname })
        });
      } else {
        formData.append('sections', this.sectionsToRender().join(','));
        formData.append('sections_url', window.location.pathname);
        res = await fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('[cart] add failed', body);
        throw body;
      }
      // The add response doesn't include the full cart totals — but it does
      // include sections. Fetch the cart once for accurate totals, in parallel
      // with opening the drawer so it feels instant.
      const cart = await fetchJSON(`${routes.cart_url}.js`);
      cart.sections = body.sections;
      this.applyCart(cart);
      drawer.open();
      return body;
    },

    async changeItem(key, quantity) {
      // Single round trip: the change endpoint returns the updated cart AND the
      // rendered sections we ask for. No separate /cart.js or /cart?sections fetch.
      const formData = new FormData();
      formData.append('id', key);
      formData.append('quantity', String(quantity));
      formData.append('sections', this.sectionsToRender().join(','));
      formData.append('sections_url', window.location.pathname);

      const res = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const cart = await res.json().catch(() => null);
      if (!res.ok) {
        console.error('[cart] change failed', cart);
        throw cart || new Error(`Request failed: ${res.status}`);
      }
      if (cart) this.applyCart(cart);
      return cart;
    }
  };

  /* Bundle definitions from Theme settings → Cart → Bundles. One per line:
     "<bundle variant id>=<component variant id>:<qty>,<component variant id>:<qty>".
     Returns the component list for a bundle variant id, or null. */
  function bundleComponentsFor(variantId) {
    const raw = (window.theme && window.theme.bundles) || '';
    if (!variantId || !raw.trim()) return null;
    for (const line of raw.split(/[\n;]+/)) {
      const [bundleId, rest] = line.split('=').map(s => (s || '').trim());
      if (!bundleId || bundleId !== String(variantId).trim() || !rest) continue;
      const components = rest.split(',').map(part => {
        const [id, qty] = part.split(':').map(s => s.trim());
        return { id: parseInt(id, 10), qty: Math.max(1, parseInt(qty || '1', 10) || 1) };
      }).filter(c => c.id > 0);
      return components.length ? components : null;
    }
    return null;
  }

  /* ---------- Modal focus (mobile menu, cart drawer, PDP popups) ----------
     While an overlay is open: everything outside it is inert, Tab cycles
     inside its panel, and closing hands focus back to whatever opened it.
     Inert is applied to the siblings of each ancestor on the way up to
     <body>, so the overlay doesn't need to be a direct child of <body>. */
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, summary, [tabindex]:not([tabindex="-1"])';
  const modalFocus = {
    stack: [],
    // Add to cart disables its button while the request runs, which drops
    // focus to <body> before the drawer opens; remember what had it last.
    lastFocused: null,
    tabbables(panel) {
      return $$(FOCUSABLE, panel).filter(el => el.getClientRects().length && !el.closest('[inert]') && getComputedStyle(el).visibility !== 'hidden');
    },
    open(root, panel, initialFocus) {
      if (this.stack.some(m => m.root === root)) return;
      const inerted = [];
      for (let node = root; node && node !== document.body; node = node.parentElement) {
        for (const sib of node.parentElement?.children || []) {
          if (sib === node || sib.hasAttribute('inert') || sib.tagName === 'SCRIPT' || sib.tagName === 'STYLE') continue;
          sib.setAttribute('inert', '');
          inerted.push(sib);
        }
      }
      const active = document.activeElement;
      this.stack.push({ root, panel, inerted, returnTo: active && active !== document.body ? active : this.lastFocused });
      (initialFocus || this.tabbables(panel)[0] || panel).focus();
    },
    close(root, fallback) {
      const i = this.stack.findIndex(m => m.root === root);
      if (i === -1) return;
      const [m] = this.stack.splice(i, 1);
      m.inerted.forEach(el => el.removeAttribute('inert'));
      const r = m.returnTo;
      const target = r && r.isConnected && !r.disabled && !r.closest('[inert]') && !root.contains(r) ? r : fallback;
      target?.focus();
    },
    onKeydown(e) {
      const m = this.stack[this.stack.length - 1];
      if (!m || e.key !== 'Tab') return;
      const items = this.tabbables(m.panel);
      if (!items.length) { e.preventDefault(); m.panel.focus(); return; }
      const first = items[0], last = items[items.length - 1];
      const inside = m.panel.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === first)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (!inside || document.activeElement === last)) { e.preventDefault(); first.focus(); }
    }
  };
  document.addEventListener('keydown', (e) => modalFocus.onKeydown(e));
  document.addEventListener('focusin', (e) => { modalFocus.lastFocused = e.target; });

  /* ---------- Cart drawer ---------- */
  const drawer = {
    el: null,
    init() {
      this.el = $('#CartDrawer');
      if (!this.el) return;

      // Open via header button (only when cart is drawer-mode)
      $$('[data-cart-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          if (cartType !== 'drawer') return;
          e.preventDefault();
          this.open();
        });
      });
      // Close via overlay or X
      $$('[data-cart-drawer-close], [data-cart-drawer-overlay]').forEach(el => {
        el.addEventListener('click', () => this.close());
      });
      // Esc
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    },
    open() {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'false');
      this.el.removeAttribute('inert');
      document.documentElement.style.overflow = 'hidden';
      const panel = this.el.querySelector('.cart-drawer__panel');
      modalFocus.open(this.el, panel, panel.querySelector('[data-cart-drawer-close]'));
      // The drawer was display:none before opening, so clientWidth was 0 and
      // the initial Liquid-rendered progress message wasn't measurable. Fit
      // it now that the drawer is laid out.
      const msgEl = this.el.querySelector('[data-cart-progress-message]');
      if (msgEl) fitProgressMessage(msgEl);
    },
    close() {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'true');
      this.el.setAttribute('inert', '');
      document.documentElement.style.overflow = '';
      modalFocus.close(this.el, $('[data-cart-toggle]'));
    },
    isOpen() { return this.el?.getAttribute('aria-hidden') === 'false'; }
  };

  /* ---------- Mobile menu ---------- */
  const mobileMenu = {
    el: null,
    init() {
      this.el = $('#MobileMenu');
      if (!this.el) return;
      // Lift out of the sticky header's stacking context so z-index works
      // against the document root, not the header wrapper.
      if (this.el.parentElement !== document.body) {
        document.body.appendChild(this.el);
      }
      $$('[data-mobile-menu-toggle]').forEach(el => {
        el.addEventListener('click', () => this.toggle());
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    },
    toggle() {
      this.isOpen() ? this.close() : this.open();
    },
    open() {
      this.el.setAttribute('aria-hidden', 'false');
      this.el.removeAttribute('inert');
      document.documentElement.style.overflow = 'hidden';
      $$('[data-mobile-menu-toggle][aria-expanded]').forEach(b => b.setAttribute('aria-expanded', 'true'));
      const panel = this.el.querySelector('.mobile-menu__panel');
      modalFocus.open(this.el, panel, panel.querySelector('.mobile-menu__close'));
      // Re-sync any mega menu scrollers inside the drawer — clientWidth was 0
      // while the drawer was display: none, so the initial visibility check
      // would have always hidden the chevrons.
      requestAnimationFrame(() => {
        $$('[data-mega-scroller]', this.el).forEach(sc => { if (sc._megaSync) sc._megaSync(); });
      });
    },
    close() {
      this.el.setAttribute('aria-hidden', 'true');
      this.el.setAttribute('inert', '');
      document.documentElement.style.overflow = '';
      $$('[data-mobile-menu-toggle][aria-expanded]').forEach(b => b.setAttribute('aria-expanded', 'false'));
      modalFocus.close(this.el, $('[data-mobile-menu-toggle][aria-expanded]'));
    },
    isOpen() { return this.el?.getAttribute('aria-hidden') === 'false'; }
  };

  /* ---------- Search toggle + predictive ---------- */
  const search = {
    init() {
      const panel = $('#SiteSearch');
      if (!panel) return;
      $$('[data-search-toggle]').forEach(btn => {
        btn.addEventListener('click', () => {
          const isHidden = panel.hasAttribute('hidden');
          if (isHidden) {
            panel.removeAttribute('hidden');
            panel.querySelector('input')?.focus();
          } else {
            panel.setAttribute('hidden', '');
          }
        });
      });

      const input = $('[data-predictive-search-input]');
      const results = $('[data-predictive-search-results]');
      if (!input || !results || !routes.predictive_search_url) return;

      const run = debounce(async () => {
        const q = input.value.trim();
        if (q.length < 2) { results.innerHTML = ''; return; }
        try {
          const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url);
          if (!res.ok) return;
          const html = await res.text();
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          // Fall back to JSON if section_id wasn't configured server-side
          if (tmp.querySelector('.predictive-search') || tmp.querySelector('.product-grid')) {
            results.innerHTML = tmp.innerHTML;
            return;
          }
          const json = await fetchJSON(`${routes.predictive_search_url}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`);
          const products = json.resources?.results?.products || [];
          results.innerHTML = products.map(p => `
            <a class="card-product" href="${p.url}">
              <div class="card-product__media">
                ${p.image ? `<img src="${p.image}" alt="${p.title}" loading="lazy">` : ''}
              </div>
              <div class="card-product__info">
                <h3 class="card-product__title">${p.title}</h3>
                <span class="price__current">${formatMoney(p.price * 100)}</span>
              </div>
            </a>
          `).join('');
        } catch (_) {
          results.innerHTML = '';
        }
      }, 220);

      input.addEventListener('input', run);
    }
  };

  /* ---------- Quick-add forms (cards) ---------- */
  let quickAddBound = false;
  function bindQuickAddForms() {
    if (quickAddBound) return;
    quickAddBound = true;
    // Delegated submit handler so forms added/cloned after init (e.g., by
    // the featured-collection infinite carousel) are also intercepted.
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-quick-add]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('button[type="submit"]');
      btn?.setAttribute('disabled', '');
      try {
        await cartState.addItem(form);
      } catch (err) {
        console.error('[cart] add failed', err);
        alert(err?.description || err?.message || 'Could not add to cart');
      } finally {
        btn?.removeAttribute('disabled');
      }
    });

    // Footnote under Add to cart ("24 BAGS · $35.98 · $1.49 / bag"). Both
    // figures are rendered per variant in snippets/card-product.liquid — pack
    // price is variant price x min_qty, per bag divides that by bags x min_qty
    // — so switching packs just copies them across and nothing here has to
    // redo that arithmetic or the money formatting. A blank per-bag value
    // means the variant has no bag count: hide the span rather than leave the
    // previous variant's number standing.
    const updateCardFootnote = (form, source) => {
      const packEl = form.querySelector('.card-product__atc-price');
      const packPrice = source.dataset.variantPackPrice;
      if (packEl && packPrice) packEl.textContent = packPrice;
      const perBagEl = form.querySelector('.card-product__atc-per-bag');
      if (!perBagEl) return;
      const perBag = source.dataset.variantPerBag || '';
      perBagEl.textContent = perBag;
      perBagEl.hidden = perBag.length === 0;
    };

    // Variant pill click on collection cards → swap the hidden variant id,
    // mark the pill as selected, and update the displayed price.
    const handleVariantPillClick = (e) => {
      const pill = e.target.closest('.card-product__variant');
      if (!pill || pill.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      const form = pill.closest('[data-card-form]');
      if (!form) return;
      const card = form.closest('.card-product');
      const variantId = pill.dataset.variantId;
      const newPrice = pill.dataset.variantPrice;
      const newCompare = pill.dataset.variantComparePrice;

      // 1. Swap the hidden variant id used by Add to cart
      const input = form.querySelector('[data-card-variant-input]');
      if (input && variantId) input.value = variantId;

      // 2. Visually mark this pill as selected
      form.querySelectorAll('.card-product__variant').forEach(p => {
        const active = p === pill;
        p.classList.toggle('is-selected', active);
        p.setAttribute('aria-checked', active ? 'true' : 'false');
      });

      // 3. Update the footnote under Add to cart
      updateCardFootnote(form, pill);

      // 4. Update the displayed price on this card
      if (!card) return;
      const current = card.querySelector('.price__current');
      if (current && newPrice) {
        current.textContent = newPrice;
      }
      const priceWrap = card.querySelector('.price');
      let compare = card.querySelector('.price__compare');
      if (newCompare && newCompare.length > 0) {
        if (compare) {
          compare.textContent = newCompare;
          compare.style.display = '';
        } else if (priceWrap && current) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          compare.textContent = newCompare;
          priceWrap.insertBefore(compare, current);
        }
        if (priceWrap) priceWrap.classList.add('price--on-sale');
      } else if (compare) {
        compare.style.display = 'none';
        if (priceWrap) priceWrap.classList.remove('price--on-sale');
      }
    };
    document.addEventListener('click', handleVariantPillClick);

    // Variant DROPDOWN change handler — same effect as pill click but for <select>
    document.addEventListener('change', (e) => {
      const sel = e.target.closest('[data-card-variant-select]');
      if (!sel) return;
      const form = sel.closest('[data-card-form]');
      if (!form) return;
      const card = form.closest('.card-product');
      const opt = sel.options[sel.selectedIndex];
      const variantId = opt.value;
      const newPrice = opt.dataset.variantPrice;
      const newCompare = opt.dataset.variantComparePrice;

      const input = form.querySelector('[data-card-variant-input]');
      if (input && variantId) input.value = variantId;

      updateCardFootnote(form, opt);

      if (!card) return;
      const current = card.querySelector('.price__current');
      if (current && newPrice) current.textContent = newPrice;
      const priceWrap = card.querySelector('.price');
      let compare = card.querySelector('.price__compare');
      if (newCompare && newCompare.length > 0) {
        if (compare) {
          compare.textContent = newCompare;
          compare.style.display = '';
        } else if (priceWrap && current) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          compare.textContent = newCompare;
          priceWrap.insertBefore(compare, current);
        }
        if (priceWrap) priceWrap.classList.add('price--on-sale');
      } else if (compare) {
        compare.style.display = 'none';
        if (priceWrap) priceWrap.classList.remove('price--on-sale');
      }
    });
  }

  /* ---------- Cart line item bindings (delegated, survive innerHTML refresh) ---------- */
  let cartEventsBound = false;
  function bindCartItemEvents() {
    if (cartEventsBound) return;
    cartEventsBound = true;

    document.addEventListener('click', async (e) => {
      // Remove button
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.key;
        if (!key) return;
        // A bundle part (e.g. half of a Variety Pack) takes the rest of its bundle with it.
        const cart = cartState.cart || await fetchJSON(`${routes.cart_url}.js`);
        const line = (cart.items || []).find(i => i.key === key);
        const bundle = line?.properties?._bundle;
        if (bundle) {
          const updates = {};
          cart.items.filter(i => i.properties?._bundle === bundle).forEach(i => { updates[i.key] = 0; });
          try {
            const res = await fetch(`${routes.cart_update_url}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ updates, sections: cartState.sectionsToRender().join(','), sections_url: window.location.pathname })
            });
            const fresh = await res.json();
            if (res.ok) cartState.applyCart(fresh);
          } catch (err) {
            console.error('[cart] bundle remove failed', err);
          }
          return;
        }
        // Optimistic: instantly collapse the row so removal feels immediate.
        // applyCart() will re-render the authoritative state when the request returns.
        const row = removeBtn.closest('[data-cart-item]');
        if (row) {
          row.style.transition = 'opacity 0.15s ease';
          row.style.opacity = '0.4';
          row.style.pointerEvents = 'none';
        }
        try {
          await cartState.changeItem(key, 0);
        } catch (err) {
          console.error('[cart] remove failed', err);
          if (row) { row.style.opacity = ''; row.style.pointerEvents = ''; }
        }
        return;
      }

      // Toggle the multi-plan upgrade menu (expands the frequency picker)
      const upgradeToggle = e.target.closest('[data-upgrade-toggle]');
      if (upgradeToggle) {
        e.preventDefault();
        const wrap = upgradeToggle.closest('[data-upgrade-wrap]');
        const menu = wrap && wrap.querySelector('[data-upgrade-menu]');
        if (menu) {
          const isHidden = menu.hidden;
          menu.hidden = !isHidden;
          upgradeToggle.setAttribute('aria-expanded', String(isHidden));
        }
        return;
      }

      // Subscribe / one-time switch: set or clear the line's selling plan. The
      // last change passes `sections=` so the response includes the freshly
      // rendered cart drawer (per-bag pricing and other Liquid-only output).
      const upgradeBtn = e.target.closest('[data-cart-upgrade-subscription]');
      const downgradeBtn = e.target.closest('[data-cart-downgrade-subscription]');
      const planBtn = upgradeBtn || downgradeBtn;
      if (planBtn) {
        e.preventDefault();
        const plan = upgradeBtn ? planBtn.dataset.plan : '';
        // A bundle row switches every component line, so a Variety Pack is
        // never half subscribed. Each change keeps the line's quantity, or
        // Shopify resets it to 1.
        const keys = (planBtn.dataset.keys || planBtn.dataset.key || '').split(',').filter(Boolean);
        if (!keys.length || (upgradeBtn && !plan)) return;
        planBtn.disabled = true;
        try {
          const toggleCart = cartState.cart || await fetchJSON(`${routes.cart_url}.js`);
          let cart = null;
          for (const [i, key] of keys.entries()) {
            const formData = new FormData();
            formData.append('id', key);
            formData.append('selling_plan', plan);
            const toggleLine = (toggleCart.items || []).find(l => l.key === key);
            if (toggleLine) formData.append('quantity', toggleLine.quantity);
            if (i === keys.length - 1) {
              formData.append('sections', cartState.sectionsToRender().join(','));
              formData.append('sections_url', window.location.pathname);
            }
            const res = await fetch(`${routes.cart_change_url}.js`, {
              method: 'POST',
              headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: formData
            });
            cart = await res.json().catch(() => null);
            if (!res.ok) throw cart || new Error('change failed');
          }
          if (cart) cartState.applyCart(cart);
        } catch (err) {
          console.error(`[cart] subscription ${upgradeBtn ? 'upgrade' : 'downgrade'} failed`, err);
          planBtn.disabled = false;
        }
        return;
      }

      // Quantity decrement
      const dec = e.target.closest('[data-quantity-decrement]');
      if (dec) {
        const wrap = dec.closest('.quantity-input');
        const input = wrap?.querySelector('[data-quantity-input], input[name="quantity"], input[name="updates[]"]');
        if (!input) return;
        const next = Math.max(parseInt(input.min || '0', 10), (parseInt(input.value, 10) || 1) - 1);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      // Quantity increment
      const inc = e.target.closest('[data-quantity-increment]');
      if (inc) {
        const wrap = inc.closest('.quantity-input');
        const input = wrap?.querySelector('[data-quantity-input], input[name="quantity"], input[name="updates[]"]');
        if (!input) return;
        input.value = (parseInt(input.value, 10) || 0) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    document.addEventListener('change', async (e) => {
      // Gift card amount picker — swap variant by removing the old line and
      // adding the new variant (Shopify doesn't let you change a variant in place).
      const giftSelect = e.target.closest('[data-gift-card-amount]');
      if (giftSelect) {
        const key = giftSelect.dataset.key;
        const newVariantId = giftSelect.value;
        if (!key || !newVariantId) return;
        giftSelect.disabled = true;
        try {
          // Read current quantity from cart
          const cart = await fetchJSON(`${routes.cart_url}.js?_=${Date.now()}`);
          const lineItem = (cart.items || []).find(i => i.key === key);
          const qty = lineItem ? lineItem.quantity : 1;
          // Remove existing line
          await fetchJSON(`${routes.cart_change_url}.js`, {
            method: 'POST',
            body: JSON.stringify({ id: key, quantity: 0 })
          });
          // Add the new variant
          await fetchJSON(`${routes.cart_add_url}.js`, {
            method: 'POST',
            body: JSON.stringify({ id: parseInt(newVariantId, 10), quantity: qty })
          });
          await cartState.refreshDrawer();
        } catch (err) {
          console.error('[cart] gift card variant change failed', err);
          giftSelect.disabled = false;
        }
        return;
      }

      const input = e.target.closest('[data-quantity-input]');
      if (!input) return;
      const key = input.dataset.key;
      if (!key) return;
      let qty = parseInt(input.value, 10) || 0;
      // A step that would leave the cart under the order minimum removes the
      // line instead (applyCart then tops up whatever is left).
      const line = (cartState.cart?.items || []).find(i => i.key === key);
      if (line && !line.properties?._bundle && qty > 0 && qty < line.quantity) {
        const after = cartBoxes(cartState.cart) - lineBoxes({ ...line, quantity: line.quantity - qty });
        if (after < minCartBoxes()) qty = 0;
      }
      try {
        await cartState.changeItem(key, qty);
      } catch (err) {
        console.error('[cart] quantity change failed', err);
      }
    });
  }

  /* ---------- Product page: gallery + variant selector ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
    async onSubmit(e) {
      e.preventDefault();
      const btn = this.form.querySelector('[data-add-to-cart]');
      btn?.setAttribute('disabled', '');
      try {
        await cartState.addItem(this.form);
      } catch (err) {
        console.error('[cart] add failed', err);
        alert(err?.description || 'Could not add to cart');
      } finally {
        btn?.removeAttribute('disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  class VariantSelector extends HTMLElement {
    connectedCallback() {
      try {
        this.product = this.dataset.giftAmounts ? this.giftCardProduct() : JSON.parse(this.dataset.product);
      } catch { this.product = null; }
      if (!this.product) return;

      this.addEventListener('change', this.onChange.bind(this));
      if (this.dataset.giftAmounts) this.addEventListener('input', this.onChange.bind(this));
    }

    // Gift card: data-gift-amounts maps whole dollars to variant ids, one hidden
    // variant per amount, so "Other" resolves to a real variant like a pill does.
    giftCardProduct() {
      const amounts = JSON.parse(this.dataset.giftAmounts);
      return {
        variants: Object.entries(amounts).map(([dollars, id]) => ({
          id, price: Number(dollars) * 100, available: true, title: '$' + dollars, options: [dollars],
        })),
      };
    }

    onChange(event) {
      const selected = this.getSelectedOptions();
      const variant = this.product.variants.find(v =>
        v.options.every((opt, i) => opt === selected[i])
      );
      if (this.dataset.giftAmounts) {
        const other = $('[data-gift-amount-other]', this);
        const custom = $('[data-gift-amount-custom]', this);
        const input = $('[data-gift-amount-input]', this);
        if (custom) custom.hidden = !other?.checked;
        if (other?.checked && event?.target === other) input?.focus();
        if (other?.checked) custom?.classList.toggle('is-invalid', !!input?.value && !variant);
        const addBtn = document.querySelector('[data-add-to-cart]');
        if (addBtn) addBtn.disabled = !variant;
      }
      if (!variant) return;

      // Update hidden id input
      const idInput = document.querySelector('[data-variant-id-input]');
      if (idInput) idInput.value = variant.id;

      // Update standalone price element (legacy, if present)
      const priceEl = document.querySelector('[data-current-price]');
      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      // Update One-Time Purchase price in the selling-plan picker
      const onetimePriceEl = document.querySelector('[data-onetime-price]');
      if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(variant.price);

      // Update Subscribe & Save price (uses the first selling plan's per-delivery
      // price for this variant). Also update the strikethrough original price.
      const picker = document.querySelector('selling-plan-picker');
      const subscribePriceEl = document.querySelector('[data-subscribe-price]');
      const originalPriceEl = document.querySelector('.selling-plans__price-original');
      if (subscribePriceEl) {
        // Find this variant's allocation for the picker's default plan
        const planIdInput = picker?.querySelector('[data-selling-plan-input]');
        const defaultPlanId = planIdInput?.value || (variant.selling_plan_allocations?.[0]?.selling_plan_id);
        const alloc = variant.selling_plan_allocations?.find(
          a => String(a.selling_plan_id) === String(defaultPlanId)
        ) || variant.selling_plan_allocations?.[0];
        const subPrice = alloc ? alloc.per_delivery_price : variant.price;
        subscribePriceEl.textContent = formatMoney(subPrice);
      }
      if (originalPriceEl) originalPriceEl.textContent = formatMoney(variant.price);

      // Subscribe-first cards layout: update every plan card's prices + per-bag
      // + savings ribbon, the buy-once link, and the add-button if a card is
      // currently selected. The existing data-onetime/subscribe handlers above
      // only target the One-Time vs Subscribe layout's elements.
      if (picker && picker.dataset.layout === 'cards') {
        const perBagLabel = picker.dataset.perBagLabel || 'bag';
        const _vtitleMatchCards = (variant.title || '').match(/^(\d+)/);
        const bagsForCards = _vtitleMatchCards ? parseInt(_vtitleMatchCards[1], 10) : (parseInt(picker.dataset.bagsPerUnit, 10) || 0);

        picker.querySelectorAll('[data-plan-radio]').forEach(radio => {
          const planId = radio.dataset.planId;
          const alloc = variant.selling_plan_allocations?.find(
            a => String(a.selling_plan_id) === String(planId)
          );
          if (!alloc) return;
          const planPrice = alloc.per_delivery_price;
          radio.dataset.planPrice = String(planPrice);

          const card = radio.closest('[data-plan-card]');
          if (!card) return;
          const priceEl = card.querySelector('.selling-plans__price-current');
          if (priceEl) priceEl.textContent = formatMoney(planPrice);
          const origEl = card.querySelector('.selling-plans__price-original');
          if (origEl) origEl.textContent = formatMoney(variant.price);
          const ribbonEl = card.querySelector('.selling-plans__ribbon');
          if (ribbonEl && variant.price > 0 && planPrice < variant.price) {
            const pct = Math.round((variant.price - planPrice) * 100 / variant.price);
            ribbonEl.textContent = pct + '% OFF';
          }
          if (bagsForCards > 1) {
            const perBagEl = card.querySelector('.selling-plans__per-unit');
            if (perBagEl) perBagEl.textContent = formatMoney(Math.round(planPrice / bagsForCards)) + ' / ' + perBagLabel;
          }
        });

        // "Buy once for $X" small link below the Add to cart button.
        const buyOncePriceEl = document.querySelector('[data-buy-once-price]');
        if (buyOncePriceEl) buyOncePriceEl.textContent = formatMoney(variant.price);

        // Sync the Add to cart button price to the currently-selected plan.
        const selectedCard = picker.querySelector('[data-plan-card].is-selected');
        const selectedRadio = selectedCard?.querySelector('[data-plan-radio]');
        const selPrice = selectedRadio?.dataset.planPrice;
        if (selPrice) {
          const currentPriceEl = document.querySelector('[data-current-price]');
          if (currentPriceEl) currentPriceEl.textContent = formatMoney(parseInt(selPrice, 10));
        }
      }

      // Update per-bag labels. Resolve bag count from the variant title first
      // (e.g. "24 BAGS" → 24), falling back to the picker's data attribute
      // (which itself came from the product's `theme.bags_per_unit` metafield).
      const _vtitleMatch = (variant.title || '').match(/^(\d+)/);
      const bagsPerUnit = _vtitleMatch ? parseInt(_vtitleMatch[1], 10) : (parseInt(picker?.dataset.bagsPerUnit, 10) || 0);
      if (picker && bagsPerUnit > 0) picker.dataset.bagsPerUnit = String(bagsPerUnit);
      if (bagsPerUnit > 1) {
        const perBagLabel = picker?.dataset.perBagLabel || 'bag';
        const onetimePerBagEl = document.querySelector('[data-onetime-per-bag]');
        if (onetimePerBagEl) {
          onetimePerBagEl.textContent = formatMoney(Math.round(variant.price / bagsPerUnit)) + ' / ' + perBagLabel;
        }
        const subscribePerBagEl = document.querySelector('[data-subscribe-per-bag]');
        if (subscribePerBagEl) {
          const planIdInput2 = picker?.querySelector('[data-selling-plan-input]');
          const defaultPlanId2 = planIdInput2?.value || (variant.selling_plan_allocations?.[0]?.selling_plan_id);
          const alloc2 = variant.selling_plan_allocations?.find(
            a => String(a.selling_plan_id) === String(defaultPlanId2)
          ) || variant.selling_plan_allocations?.[0];
          const subCents = alloc2 ? alloc2.per_delivery_price : variant.price;
          subscribePerBagEl.textContent = formatMoney(Math.round(subCents / bagsPerUnit)) + ' / ' + perBagLabel;
        }
      }

      // If selling-plan picker is in subscribe mode, sync the cart-form price + button
      // with the subscribe amount instead of one-time
      if (picker && picker.querySelector('[data-mode="subscribe"].is-selected')) {
        const subBtnPriceEl = document.querySelector('[data-add-price]');
        if (subBtnPriceEl && subscribePriceEl) subBtnPriceEl.textContent = subscribePriceEl.textContent;
      }

      // Update add-to-cart button availability
      const addBtn = document.querySelector('[data-add-to-cart]');
      if (addBtn) {
        addBtn.disabled = !variant.available;
        const lbl = addBtn.querySelector('[data-add-label]');
        if (lbl && !variant.available) lbl.textContent = window.theme?.soldOut || 'Sold out';
      }

      // Update featured media
      if (variant.featured_media?.id) {
        const sel = `[data-media-id="${variant.featured_media.id}"]`;
        $$('.product-gallery__slide').forEach(s => s.classList.remove('is-active'));
        $$('.product-gallery__thumb').forEach(t => t.classList.remove('is-active'));
        $$('.product-gallery__dot').forEach(d => { d.classList.remove('is-active'); d.setAttribute('aria-selected', 'false'); });
        $(`.product-gallery__slide${sel}`)?.classList.add('is-active');
        $(`.product-gallery__thumb${sel}`)?.classList.add('is-active');
        const activeDot = $(`.product-gallery__dot${sel}`);
        if (activeDot) { activeDot.classList.add('is-active'); activeDot.setAttribute('aria-selected', 'true'); }
      }

      // Update URL without reload
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    getSelectedOptions() {
      if (this.dataset.giftAmounts) {
        const checked = $('input[name="gift_card_amount"]:checked', this);
        if (!checked) return [];
        if (checked.value !== 'other') return [checked.value];
        const dollars = Number($('[data-gift-amount-input]', this)?.value);
        return Number.isInteger(dollars) ? [String(dollars)] : [];
      }
      return $$('input[data-variant-option]:checked', this).map(input => input.value);
    }
  }
  customElements.define('variant-selector', VariantSelector);

  /* ---------- Selling plan picker (subscribe vs one-time + frequency) ---------- */
  class SellingPlanPicker extends HTMLElement {
    connectedCallback() {
      this.hiddenInput = this.querySelector('[data-selling-plan-input]');
      this.options = $$('[data-mode]', this);
      this.frequencyDetails = this.querySelector('[data-frequency]');
      this.frequencyLabel = this.querySelector('[data-frequency-label]');
      this.subscribePriceEl = this.querySelector('[data-subscribe-price]');
      this.subscribeOption = this.querySelector('[data-mode="subscribe"]');
      this.selectedPlanId = '';

      // Initialize selected plan id. Prefer the hidden input's value (set by
      // Liquid from the default selling plan) so products with only ONE plan
      // — which don't render the frequency list — still know the plan ID.
      // Fall back to the first frequency item when multiple plans are listed.
      if (this.hiddenInput && this.hiddenInput.value) {
        this.selectedPlanId = this.hiddenInput.value;
      } else {
        const firstFreq = this.querySelector('.selling-plans__frequency-item');
        if (firstFreq) this.selectedPlanId = firstFreq.dataset.planId || '';
      }

      // Sync mode to whichever radio is checked at page load (so when Liquid
      // defaults to subscribe, the hidden input + price update accordingly)
      const checkedRadio = this.querySelector('[data-mode-radio]:checked');
      if (checkedRadio) this.setMode(checkedRadio.value);

      this.addEventListener('change', (e) => {
        const modeRadio = e.target.closest('[data-mode-radio]');
        if (modeRadio) this.setMode(modeRadio.value);

        // Subscribe-first cards layout: each plan is its own radio.
        const planRadio = e.target.closest('[data-plan-radio]');
        if (planRadio) this.selectPlanCard(planRadio);
      });

      this.addEventListener('click', (e) => {
        const freqBtn = e.target.closest('.selling-plans__frequency-item');
        if (freqBtn) {
          e.preventDefault();
          this.selectFrequency(freqBtn);
        }
      });

      // Sync initial state for the cards layout (first card pre-checked in Liquid).
      if (this.dataset.layout === 'cards') {
        const checkedCard = this.querySelector('[data-plan-radio]:checked');
        if (checkedCard) this.selectPlanCard(checkedCard);
      }
    }

    selectPlanCard(radio) {
      const card = radio.closest('[data-plan-card]');
      if (!card) return;
      const planId = radio.dataset.planId;
      const planPrice = radio.dataset.planPrice;
      if (!planId) return;

      this.selectedPlanId = planId;
      if (this.hiddenInput) this.hiddenInput.value = planId;

      // Mark this card selected, all others not.
      $$('[data-plan-card]', this).forEach(c => c.classList.toggle('is-selected', c === card));

      // Update the Add-to-cart price tracker so other code (e.g. payment button)
      // sees the right amount.
      if (planPrice) this.updateAddPrice(formatMoney(parseInt(planPrice, 10)));

      this.dispatchEvent(new CustomEvent('selling-plan:change', { bubbles: true, detail: { mode: 'subscribe', planId } }));
    }

    setMode(mode) {
      this.options.forEach(opt => opt.classList.toggle('is-selected', opt.dataset.mode === mode));
      if (mode === 'subscribe') {
        this.hiddenInput.value = this.selectedPlanId;
        const subPriceEl = this.querySelector('[data-subscribe-price]');
        this.updateAddPrice(subPriceEl?.textContent);
      } else {
        this.hiddenInput.value = '';
        const oneTimePriceEl = this.querySelector('[data-onetime-price]');
        this.updateAddPrice(oneTimePriceEl?.textContent);
      }
      this.dispatchEvent(new CustomEvent('selling-plan:change', { bubbles: true, detail: { mode, planId: this.hiddenInput.value } }));
    }

    updateAddPrice(priceText) {
      if (!priceText) return;
      const priceEl = document.querySelector('[data-current-price]');
      if (priceEl) priceEl.textContent = priceText;
    }

    selectFrequency(btn) {
      const planId = btn.dataset.planId;
      const planName = btn.dataset.planName;
      const planPrice = btn.dataset.planPrice;

      this.selectedPlanId = planId;
      // Mark the subscribe mode active
      const subscribeRadio = this.subscribeOption.querySelector('[data-mode-radio]');
      if (subscribeRadio && !subscribeRadio.checked) {
        subscribeRadio.checked = true;
        this.setMode('subscribe');
      } else {
        this.hiddenInput.value = planId;
      }

      if (this.frequencyLabel) this.frequencyLabel.textContent = planName;
      if (this.subscribePriceEl && planPrice) {
        const formatted = formatMoney(parseInt(planPrice, 10));
        this.subscribePriceEl.textContent = formatted;
        this.updateAddPrice(formatted);
      }

      // Keep the subscribe per-bag label in sync with the picked frequency.
      const bagsPerUnit = parseInt(this.dataset.bagsPerUnit, 10) || 0;
      if (bagsPerUnit > 1 && planPrice) {
        const perBagLabel = this.dataset.perBagLabel || 'bag';
        const subscribePerBagEl = this.querySelector('[data-subscribe-per-bag]');
        if (subscribePerBagEl) {
          subscribePerBagEl.textContent = formatMoney(Math.round(parseInt(planPrice, 10) / bagsPerUnit)) + ' / ' + perBagLabel;
        }
      }
      // Update selected state on items
      $$('.selling-plans__frequency-item', this).forEach(item => {
        item.classList.toggle('is-selected', item === btn);
      });
      // Collapse the details
      if (this.frequencyDetails) this.frequencyDetails.open = false;

      this.dispatchEvent(new CustomEvent('selling-plan:change', { bubbles: true, detail: { mode: 'subscribe', planId } }));
    }
  }
  customElements.define('selling-plan-picker', SellingPlanPicker);

  /* Buy-once link (subscribe-first layout): clear the selling_plan input
     synchronously on click so the form submission that follows adds the
     item as a one-time purchase. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-buy-once]');
    if (!btn) return;
    const picker = document.querySelector('selling-plan-picker');
    if (picker) {
      const input = picker.querySelector('[data-selling-plan-input]');
      if (input) input.value = '';
    }
  }, { capture: true });

  function bindGalleryThumbs() {
    const main = $('[data-gallery-main]');
    if (!main) return;

    const originals = $$('.product-gallery__slide', main);

    // Helper: sync active class on both thumbs and dots
    let activeMediaId = originals[0]?.dataset.mediaId;
    const setActive = (id) => {
      activeMediaId = id;
      $$('.product-gallery__thumb').forEach(t => t.classList.toggle('is-active', t.dataset.mediaId === id));
      $$('.product-gallery__dot').forEach(d => {
        const active = d.dataset.mediaId === id;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    // For 0–1 slides nothing to scroll; bail after basic setup.
    if (originals.length < 2) return;

    /* Seamless infinite scroll: clone the original slides once on each
       side. When the gallery settles on a clone after a user swipe, we
       silently snap scrollLeft to the equivalent original — clones look
       identical so the swap is invisible. */
    const len = originals.length;
    // Prepend left clones in reverse so they appear in original order.
    for (let i = len - 1; i >= 0; i--) {
      const c = originals[i].cloneNode(true);
      c.dataset.galleryClone = 'left';
      c.setAttribute('aria-hidden', 'true');
      main.insertBefore(c, main.firstChild);
    }
    originals.forEach(s => {
      const c = s.cloneNode(true);
      c.dataset.galleryClone = 'right';
      c.setAttribute('aria-hidden', 'true');
      main.appendChild(c);
    });

    // Compute the scrollLeft that centers a given slide inside the
    // gallery viewport. In non-peek mode (slide width = 100%) the center
    // offset is 0, so this degrades to slide.offsetLeft — identical to
    // the previous behavior.
    const scrollTargetFor = (slide) => {
      const centerOffset = (main.clientWidth - slide.offsetWidth) / 2;
      return slide.offsetLeft - centerOffset;
    };

    // Seed scroll past the left clones to the first real slide.
    let programmaticLeft = null; // last scrollLeft we set ourselves (see onUserScroll)
    const seedScroll = () => {
      const prev = main.style.scrollBehavior;
      main.style.scrollBehavior = 'auto';
      main.scrollLeft = scrollTargetFor(originals[0]);
      programmaticLeft = main.scrollLeft;
      requestAnimationFrame(() => { main.style.scrollBehavior = prev; });
    };
    requestAnimationFrame(seedScroll);
    window.addEventListener('load', seedScroll, { once: true });

    // scrollLeft is in pixels, so a width change (rotation, window resize,
    // the layout settling) leaves the gallery between slides and a strip
    // of the neighbour shows at the edge. Re-align to the active slide.
    window.addEventListener('resize', debounce(() => {
      const target = originals.find(s => s.dataset.mediaId === activeMediaId) || originals[0];
      const prev = main.style.scrollBehavior;
      main.style.scrollBehavior = 'auto';
      main.scrollLeft = scrollTargetFor(target);
      programmaticLeft = main.scrollLeft;
      requestAnimationFrame(() => { main.style.scrollBehavior = prev; });
    }, 120));

    // Find the slide whose CENTER is closest to the viewport's center —
    // works for both full-width slides and the peek carousel layout where
    // slides are narrower than the viewport.
    const allSlides = () => $$('.product-gallery__slide', main);

    // Slides past the first ship with data-src/data-srcset (main-product.liquid):
    // hydrate the active slide and its neighbours now, everything else on the
    // first swipe or scroll, so phones don't download the whole gallery up front.
    const hydrate = (slide) => {
      if (!slide) return;
      $$('img[data-src]', slide).forEach(img => {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        delete img.dataset.src; delete img.dataset.srcset;
      });
    };
    const hydrateAll = () => allSlides().forEach(hydrate);
    (() => { const all = allSlides(); [len - 1, len, len + 1].forEach(i => hydrate(all[i])); })();
    // Only a real swipe counts: seedScroll/checkSnap move scrollLeft themselves
    // and would otherwise fire this on load.
    const onUserScroll = () => {
      // Before the first seed, scroll events come from Chrome's scroll anchoring as
      // the clones are inserted — not from the shopper.
      if (programmaticLeft === null || Math.abs(main.scrollLeft - programmaticLeft) < 4) return;
      main.removeEventListener('scroll', onUserScroll);
      hydrateAll();
    };
    main.addEventListener('scroll', onUserScroll, { passive: true });
    main.addEventListener('touchstart', hydrateAll, { once: true, passive: true });
    main.addEventListener('pointerdown', hydrateAll, { once: true, passive: true });
    $$('[data-gallery-thumbs] .product-gallery__thumb, [data-gallery-dots] .product-gallery__dot, [data-gallery-prev], [data-gallery-next]').forEach(el => el.addEventListener('click', hydrateAll, { once: true }));

    const closestIndex = () => {
      const all = allSlides();
      const viewportCenter = main.scrollLeft + main.clientWidth / 2;
      let best = 0, bestDist = Infinity;
      all.forEach((s, i) => {
        const slideCenter = s.offsetLeft + s.offsetWidth / 2;
        const dist = Math.abs(slideCenter - viewportCenter);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    };

    // After the user lets go (scrollend), if they landed on a clone,
    // jump silently to the equivalent original.
    let snapping = false;
    const checkSnap = () => {
      if (snapping) return;
      const all = allSlides();
      const idx = closestIndex();
      // Originals occupy [len, len*2 - 1]. Anything else is a clone.
      let target = -1;
      if (idx < len) target = idx + len;          // left clone → equiv original
      else if (idx >= len * 2) target = idx - len; // right clone → equiv original
      if (target < 0) return;
      const t = all[target];
      if (!t) return;
      snapping = true;
      const prev = main.style.scrollBehavior;
      main.style.scrollBehavior = 'auto';
      main.scrollLeft = scrollTargetFor(t);
      programmaticLeft = main.scrollLeft;
      requestAnimationFrame(() => {
        main.style.scrollBehavior = prev;
        snapping = false;
      });
    };
    if ('onscrollend' in main) {
      main.addEventListener('scrollend', checkSnap);
    } else {
      main.addEventListener('scroll', debounce(checkSnap, 150), { passive: true });
    }

    // Helper: scroll the main gallery to the first original with this id
    // so the user always stays in the middle section after thumb clicks.
    const scrollToMedia = (id) => {
      const target = originals.find(s => s.dataset.mediaId === id);
      if (target) main.scrollTo({ left: scrollTargetFor(target), behavior: 'smooth' });
    };

    // Click thumbnail → scroll + activate
    $$('[data-gallery-thumbs] .product-gallery__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const id = thumb.dataset.mediaId;
        scrollToMedia(id);
        setActive(id);
      });
    });

    // Click dot → scroll + activate (mobile)
    $$('[data-gallery-dots] .product-gallery__dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const id = dot.dataset.mediaId;
        scrollToMedia(id);
        setActive(id);
      });
    });

    // Prev / Next chevrons scroll to the adjacent slide by computed
    // offsetLeft. (scrollBy with `scroll-snap-type: mandatory` can stall
    // mid-transition — scrollTo to an explicit slide is reliable.)
    const goRelative = (delta) => {
      const all = allSlides();
      const idx = closestIndex();
      const target = all[idx + delta];
      if (target) main.scrollTo({ left: scrollTargetFor(target), behavior: 'smooth' });
    };
    const prevBtn = $('[data-gallery-prev]');
    const nextBtn = $('[data-gallery-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => goRelative(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => goRelative(1));

    // Watch for swipe/scroll: highlight thumb + dot of whichever slide
    // (original or clone) is currently centered.
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          setActive(entry.target.dataset.mediaId);
        }
      });
    }, { root: main, threshold: 0.5 });
    allSlides().forEach(s => observer.observe(s));
  }

  /* ---------- Cart drawer custom element ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {}
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- Announcement bar rotator ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-announcement]', this);
      if (this.items.length <= 1) return;
      this.idx = 0;
      this.speed = (parseFloat(this.dataset.speed) || 5) * 1000;
      this.autoplay = this.dataset.autoplay !== 'false';

      $('[data-announcement-prev]', this)?.addEventListener('click', () => { this.show(this.idx - 1); this.restart(); });
      $('[data-announcement-next]', this)?.addEventListener('click', () => { this.show(this.idx + 1); this.restart(); });

      // Hover and keyboard focus hold the current message; the pause button
      // stops rotation until pressed again. Reduced motion starts paused.
      this.pauseBtn = $('[data-announcement-pause]', this);
      this.paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.pauseBtn?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.syncPauseBtn();
        this.paused ? this.stop() : this.start();
      });
      this.syncPauseBtn();

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.restart());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.restart(); });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.restart();
      });

      this.restart();
    }
    disconnectedCallback() { this.stop(); }
    start() { this.stop(); this.timer = setInterval(() => this.show(this.idx + 1), this.speed); }
    stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
    restart() { if (this.autoplay && !this.paused) this.start(); }
    syncPauseBtn() {
      if (!this.pauseBtn) return;
      this.pauseBtn.setAttribute('aria-pressed', String(this.paused));
      this.pauseBtn.classList.toggle('is-paused', this.paused);
    }
    show(i) {
      const len = this.items.length;
      const next = ((i % len) + len) % len;
      this.items[this.idx].classList.remove('is-active');
      this.items[next].classList.add('is-active');
      this.idx = next;
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-motion-pause]');
    if (!btn) return;
    const paused = btn.getAttribute('aria-pressed') !== 'true';
    btn.setAttribute('aria-pressed', String(paused));
    btn.closest('[data-motion]')?.classList.toggle('is-paused', paused);
  });

  /* ---------- Sort by (collection page) ---------- */
  function bindCollectionSort() {
    const sort = $('[data-sort-by]');
    if (!sort) return;
    sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      window.location.href = url.toString();
    });
  }

  function bindProductReviewToggle() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pr-toggle]');
      if (btn) {
        e.preventDefault();
        const modal = document.querySelector('[data-pr-modal]');
        if (!modal) return;
        modal.hidden = false;
        document.body.classList.add('pr-modal-open');
        setTimeout(() => {
          modal.querySelector('input[name="customer[first_name]"]')?.focus();
        }, 100);
        return;
      }
      const closer = e.target.closest('[data-pr-close]');
      if (closer) {
        e.preventDefault();
        const modal = closer.closest('[data-pr-modal]');
        if (modal) {
          modal.hidden = true;
          document.body.classList.remove('pr-modal-open');
        }
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const modal = document.querySelector('[data-pr-modal]:not([hidden])');
      if (modal) {
        modal.hidden = true;
        document.body.classList.remove('pr-modal-open');
      }
    });
  }

  function bindProductPickerCarousel() {
    $$('[data-pp-carousel]').forEach(carousel => {
      // Fill-row mode (≤cols products): static layout, no scroll bindings.
      if (carousel.hasAttribute('data-pp-fill-row')) return;
      const track = carousel.querySelector('[data-pp-track]');
      const prev = carousel.querySelector('[data-pp-prev]');
      const next = carousel.querySelector('[data-pp-next]');
      if (!track || !prev || !next) return;

      // How far one click should scroll: width of one item + gap
      const slideStep = () => {
        const slide = track.querySelector('li');
        if (!slide) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0);
        return slide.getBoundingClientRect().width + gap;
      };
      const updateButtons = () => {
        const max = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= max;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -slideStep(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left:  slideStep(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(updateButtons, 80), { passive: true });
      window.addEventListener('resize', debounce(updateButtons, 100));
      updateButtons();
    });
  }

  /* ---------- Footer menu accordions: collapsed on mobile, open on desktop ---------- */
  const footerMenuAccordions = {
    breakpoint: 600,
    init() {
      this.elements = $$('[data-footer-menu]');
      if (!this.elements.length) return;
      this.sync = this.sync.bind(this);
      this.sync();
      window.addEventListener('resize', debounce(this.sync, 100));
    },
    sync() {
      const isDesktop = window.innerWidth >= this.breakpoint;
      this.elements.forEach(el => {
        if (isDesktop) el.setAttribute('open', '');
      });
    }
  };

  /* ---------- Init ---------- */
  /* Both sync fns measure (layout read) then set a CSS var (write). Batch
     the write into a rAF so back-to-back calls don't force a reflow between
     each read/write pair (Lighthouse "forced reflow"). */
  let _syncRaf = null;
  const _pendingVars = {};
  function flushSyncVars() {
    _syncRaf = null;
    for (const k in _pendingVars) {
      document.documentElement.style.setProperty(k, _pendingVars[k]);
      delete _pendingVars[k];
    }
  }
  function queueSyncVar(name, value) {
    _pendingVars[name] = value;
    if (!_syncRaf) _syncRaf = requestAnimationFrame(flushSyncVars);
  }

  function syncAnnouncementHeight() {
    const bar = document.querySelector('announcement-bar');
    const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    queueSyncVar('--announcement-height', h + 'px');
  }

  function syncHeaderHeight() {
    const header = document.querySelector('.site-header');
    const h = header ? Math.round(header.getBoundingClientRect().height) : 0;
    queueSyncVar('--header-height', h + 'px');
  }

  /* Sticky Add-to-cart bar on the PDP: slides up once the shopper has
     scrolled past the main ATC button, and stays hidden before that (the
     button is either on screen or still below the fold). */
  function bindStickyAtc() {
    const bar = document.querySelector('[data-pdp-sticky]');
    if (!bar) return;
    const mainBtn = document.querySelector('[data-add-to-cart]');
    if (!mainBtn) return;
    // Portal the bar to <body> so no ancestor's transform/filter/contain
    // can trap the fixed positioning under other content. Without this
    // the bar can end up beneath the footer even with a high z-index.
    if (bar.parentElement !== document.body) {
      document.body.appendChild(bar);
    }
    bar.hidden = false;

    // Track whether the cart drawer is open — the sticky bar should tuck
    // itself away while the drawer is up so it doesn't sit on top of it.
    const drawer = document.querySelector('#CartDrawer');
    let drawerOpen = drawer ? drawer.getAttribute('aria-hidden') === 'false' : false;
    if (drawer) {
      new MutationObserver(() => {
        drawerOpen = drawer.getAttribute('aria-hidden') === 'false';
        applyState();
      }).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
    const applyState = () => {
      if (drawerOpen) {
        bar.classList.remove('is-visible');
        return;
      }
      const rect = mainBtn.getBoundingClientRect();
      const scrolledPast = rect.bottom < 0;
      bar.classList.toggle('is-visible', scrolledPast);
      // Reserve the bar's height at the page bottom while it's up so it
      // never covers the footer's copyright line at full scroll.
      document.body.style.paddingBottom = scrolledPast ? bar.offsetHeight + 'px' : '';
    };
    applyState();
    window.addEventListener('scroll', applyState, { passive: true });
    window.addEventListener('resize', debounce(applyState, 100));

    // Clicking the sticky button triggers the real Add to cart button so
    // it flows through the same form submission + drawer refresh path.
    // Hide the bar immediately so it doesn't briefly sit on top of the
    // drawer while the aria-hidden observer catches up.
    const stickyBtn = bar.querySelector('[data-pdp-sticky-atc]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        bar.classList.remove('is-visible');
        mainBtn.click();
      });
    }
  }

  /* Shipping-country notice — Shopify only knows a market country, and
     visitors from countries without a market browse as the US. Ask Shopify's
     geo lookup once per session; outside the ship-to list, html gets
     .ships-unavailable and every .ship-notice in the cart becomes visible. */
  function bindShipNotice() {
    const list = String((window.theme && window.theme.shipToCountries) || 'US,CA').toUpperCase().replace(/\s+/g, '').split(',').filter(Boolean);
    const apply = (code) => {
      if (code) document.documentElement.classList.toggle('ships-unavailable', !list.includes(String(code).toUpperCase()));
    };
    let cached = '';
    try { cached = sessionStorage.getItem('gk_geo_country') || ''; } catch (e) {}
    if (cached) { apply(cached); return; }
    fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const code = d && d.detected_values && d.detected_values.country && d.detected_values.country.handle;
        if (!code) return;
        try { sessionStorage.setItem('gk_geo_country', code); } catch (e) {}
        apply(code);
      })
      .catch(() => {});
  }

  /* Product callouts popup — small button that opens an image in a
     lightbox. Clicking the overlay, close button, or Escape closes it.
     Portals the modal to <body> so no ancestor stacking context can trap
     it below sibling content (e.g. the product gallery's next-arrow). */
  function bindCalloutsModal() {
    const modal = document.querySelector('[data-callouts-modal]');
    if (!modal) return;
    const openBtn = document.querySelector('[data-callouts-modal-open]');
    if (!openBtn) return;
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    const panel = modal.querySelector('[role="dialog"]');
    const close = () => {
      modal.hidden = true;
      document.documentElement.style.overflow = '';
      modalFocus.close(modal, openBtn);
    };
    const open = () => {
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      modalFocus.open(modal, panel, panel.querySelector('[data-callouts-modal-close]'));
    };
    openBtn.addEventListener('click', open);
    // Delegate closes so we catch every current + future close button
    // inside the modal (overlay button + top-right X button both work).
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-callouts-modal-close]')) close();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  /* Transparent-until-scroll header — toggle .is-scrolled once the user
     moves past a small threshold so the background can fade in. */
  function bindTransparentHeader() {
    const header = document.querySelector('.site-header[data-transparent="true"]');
    if (!header) return;
    const threshold = 8;
    const apply = () => {
      header.classList.toggle('is-scrolled', window.scrollY > threshold);
    };
    apply();
    window.addEventListener('scroll', apply, { passive: true });
  }

  function bindFeaturedCollectionCarousels() {
    $$('[data-fc-carousel]').forEach(carousel => {
      const track = carousel.querySelector('[data-fc-track]');
      const prev = carousel.querySelector('[data-fc-prev]');
      const next = carousel.querySelector('[data-fc-next]');
      if (!track) return;

      // Fill-row mode (≤5 products) — render them static, side by side. No
      // clones, no scrolling, no arrows. Liquid+CSS does the layout; JS just
      // bails out so the cards aren't duplicated.
      if (carousel.hasAttribute('data-fc-fill-row')) return;

      const originalSlides = $$('.featured-collection__slide', track);
      if (originalSlides.length < 2) return;

      // True infinite scroll, never jumps:
      // 1. Seed the track with one cloned set on each side so the user has
      //    content to scroll into immediately.
      // 2. As the user approaches either edge, append/prepend another full
      //    clone set on that side. The DOM grows, but the user's scroll
      //    position stays visually identical — for prepends we offset
      //    scrollLeft by the inserted width so nothing shifts on screen.
      const cloneSet = (side) => {
        if (side === 'left') {
          for (let i = originalSlides.length - 1; i >= 0; i--) {
            const c = originalSlides[i].cloneNode(true);
            c.dataset.fcClone = 'left';
            c.setAttribute('aria-hidden', 'true');
            track.prepend(c);
          }
        } else {
          originalSlides.forEach(s => {
            const c = s.cloneNode(true);
            c.dataset.fcClone = 'right';
            c.setAttribute('aria-hidden', 'true');
            track.appendChild(c);
          });
        }
      };
      cloneSet('left');
      cloneSet('right');

      const slideWidth = () => {
        const slide = track.querySelector('.featured-collection__slide');
        if (!slide) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0);
        return slide.getBoundingClientRect().width + gap;
      };

      // Seed scroll position so the FIRST real (un-cloned) slide lines up at
      // the left edge of the viewport. offsetLeft reads actual layout, so this
      // stays correct even if slide widths change between scripts and CSS.
      const seedScroll = () => {
        const firstReal = originalSlides[0];
        if (firstReal && firstReal.offsetParent !== null) {
          track.scrollLeft = firstReal.offsetLeft;
        } else {
          track.scrollLeft = originalSlides.length * slideWidth();
        }
      };
      requestAnimationFrame(seedScroll);
      window.addEventListener('load', seedScroll, { once: true });

      // Grow the track on demand. Triggers before the user reaches the edge
      // so there's no visible end.
      let expandScheduled = false;
      const expandIfNeeded = () => {
        expandScheduled = false;
        const buffer = slideWidth() * 4;

        // Right side: append more before the user hits the end
        if (track.scrollWidth - track.scrollLeft - track.clientWidth < buffer) {
          cloneSet('right');
        }
        // Left side: prepend more, then compensate scrollLeft so the user's
        // current view doesn't visually move
        if (track.scrollLeft < buffer) {
          const beforeWidth = track.scrollWidth;
          cloneSet('left');
          const afterWidth = track.scrollWidth;
          track.scrollLeft += (afterWidth - beforeWidth);
        }
      };

      track.addEventListener('scroll', () => {
        if (expandScheduled) return;
        expandScheduled = true;
        requestAnimationFrame(expandIfNeeded);
      }, { passive: true });

      const positionArrows = () => {
        const media = carousel.querySelector('.featured-collection__slide .card-product__media');
        if (!media) return;
        const carouselRect = carousel.getBoundingClientRect();
        const mediaRect = media.getBoundingClientRect();
        const center = (mediaRect.top - carouselRect.top) + mediaRect.height / 2;
        carousel.style.setProperty('--fc-arrow-top', center + 'px');
      };

      prev?.addEventListener('click', () => track.scrollBy({ left: -slideWidth(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left:  slideWidth(), behavior: 'smooth' }));
      window.addEventListener('resize', debounce(positionArrows, 100));

      positionArrows();
      carousel.querySelectorAll('img').forEach(img => {
        if (img.complete) return;
        img.addEventListener('load', positionArrows, { once: true });
      });
    });
  }

  function init() {
    drawer.init();
    mobileMenu.init();
    search.init();
    promoCode.init();
    bindExpressRows();
    bindQuickAddForms();
    bindCartItemEvents();
    bindGalleryThumbs();
    bindCollectionSort();
    bindProductPickerCarousel();
    bindProductReviewToggle();
    bindFeaturedCollectionCarousels();
    bindQuickAddCarousel();
    bindTestimonialsCarousel();
    bindVideoSlider();
    bindFaqTabbed();
    bindStoreLocator();
    bindMegaMenu();
    footerMenuAccordions.init();
    bindInfluencerGiftForm();
    // Inject upgrade-to-subscription buttons and gift card pickers on initial page load
    renderUpgradeButtons();
    renderGiftCardPickers();
    // One-shot free gift sync at page load so a customer arriving with an
    // already-qualifying cart gets the gift inserted (or one below threshold gets it removed).
    syncFreeGift();

    // Keep the progress message fit-to-width on viewport resize too.
    window.addEventListener('resize', debounce(() => {
      const msgEl = document.querySelector('[data-cart-progress-message]');
      if (msgEl) fitProgressMessage(msgEl);
    }, 100), { passive: true });

    syncAnnouncementHeight();
    syncHeaderHeight();
    bindTransparentHeader();
    bindStickyAtc();
    bindCalloutsModal();
    bindShipNotice();
    window.addEventListener('resize', debounce(() => { syncAnnouncementHeight(); syncHeaderHeight(); }, 100));
    const bar = document.querySelector('announcement-bar');
    if (bar && 'ResizeObserver' in window) {
      new ResizeObserver(syncAnnouncementHeight).observe(bar);
    }
    const headerEl = document.querySelector('.site-header');
    if (headerEl && 'ResizeObserver' in window) {
      new ResizeObserver(syncHeaderHeight).observe(headerEl);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   Deferred media loader
   --------------------------------------------------------------------------
   1. video[data-vl-defer] (rendered by snippets/video-lite.liquid) ships
      with no <source src>. The poster paints immediately; the mp4 is only
      attached when the video nears the viewport. Decorative autoplay loops
      stop competing with the LCP image for bandwidth.
   2. [data-tv-embed] facades (title-video section) stand in for YouTube /
      Vimeo iframes: ~830 KB of player JS + ad cookies load only when the
      visitor clicks play (or, for autoplay embeds, when scrolled into view).
   ========================================================================== */
(() => {
  const activateVideo = (v) => {
    if (v.dataset.vlDone) return;
    v.dataset.vlDone = '1';
    v.querySelectorAll('source[data-vl-src]').forEach((s) => { s.src = s.dataset.vlSrc; });
    v.load();
    // Motion-sensitive users keep the poster; the source still attaches so
    // controls-based videos remain playable.
    if (v.hasAttribute('autoplay') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // play() right after load() can be dropped while the fetch is still
      // in flight — retry once the data is actually playable.
      const tryPlay = () => {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      };
      tryPlay();
      v.addEventListener('canplay', tryPlay, { once: true });
    }
  };

  // mode: 'auto' (scrolled into view — must be muted for autoplay policies)
  // or 'click' (user gesture — sound allowed). YouTube uses mute=, Vimeo
  // uses muted=; each platform ignores the other's param.
  const buildEmbed = (facade, mode) => {
    if (facade.dataset.tvDone) return;
    facade.dataset.tvDone = '1';
    let url = facade.dataset.tvEmbed || '';
    if (!/[?&]autoplay=1/.test(url)) {
      url += (url.includes('?') ? '&' : '?') + 'autoplay=1';
      if (mode === 'auto') url += '&mute=1&muted=1';
    }
    const iframe = document.createElement('iframe');
    iframe.className = 'title-video__embed';
    iframe.src = url;
    iframe.title = facade.getAttribute('aria-label') || 'Embedded video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    facade.replaceWith(iframe);
  };

  const init = () => {
    const vids = document.querySelectorAll('video[data-vl-defer]:not([data-vl-done])');
    const facades = document.querySelectorAll('[data-tv-embed]:not([data-tv-done])');

    if ('IntersectionObserver' in window) {
      if (vids.length) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) { activateVideo(e.target); io.unobserve(e.target); }
          });
        }, { rootMargin: '200px' });
        vids.forEach((v) => io.observe(v));
      }
      facades.forEach((f) => {
        if (f.dataset.tvAutoplay === '1') {
          const fio = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) { buildEmbed(e.target, 'auto'); fio.disconnect(); }
            });
          }, { rootMargin: '100px' });
          fio.observe(f);
        }
      });
    } else {
      vids.forEach(activateVideo);
      facades.forEach((f) => { if (f.dataset.tvAutoplay === '1') buildEmbed(f, 'auto'); });
    }

    facades.forEach((f) => {
      f.addEventListener('click', () => buildEmbed(f, 'click'));
      f.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); buildEmbed(f, 'click'); }
      });
    });
  };

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }
  document.addEventListener('shopify:section:load', init);
})();
